import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { CallBtn, WABtn, PhoneField } from '../components/ui/PhoneButtons';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';
import {
  Phone, Plus, X, Edit2, Trash2, Search, ChevronDown, ChevronRight,
  Users, Building2, BarChart2, Settings, Bell, RefreshCw, Download,
  Upload, CheckCircle, Clock, AlertCircle, UserCheck, Printer,
  BookOpen, TrendingUp, Filter, Megaphone
, Eye } from 'lucide-react';

// ── Constants ──────────────────────────────────────────────────────
const STREAMS       = ['Civil','Mechanical','Automobile','Aeronautical','Electrical','Other'];
const RESPONSES     = ['Not Called','Call Not Attended','Low Interest','Medium Interest','High Interest','Demo Scheduled','Demo Attended','Demo No Show','Converted','Lost','Not Reachable','Call Back Later','Wrong Number'];
const FOLLOWUP_TYPES= ['Call','WhatsApp Message','Demo Class','Enrolment Meeting','In-Person Visit','Email'];
const SOURCES       = ['NACTET Scholarship','Reference','Call','WhatsApp','Social Media','Direct Walk-in','Other'];
const CENTRES       = ['EKM','TVM'];
const THIS_YEAR     = new Date().getFullYear();
// Years offered everywhere (year of passing / data year). Grows automatically each year.
const YEARS         = Array.from({length: THIS_YEAR + 1 - 2021 + 1}, (_, i) => 2021 + i);
const YEAR_KEY      = 'crm_data_year';

// Fetch every row (Supabase returns max 1000 per request). mk() must build a fresh query each call.
const isWinner = s => !!s?.is_winner || /winner/i.test(s?.remarks || '');
const WinnerTag = () => <span title="NACTET Winner" style={{marginLeft:6,fontSize:10,fontWeight:700,background:'#fef3c7',color:'#92400e',border:'1px solid #f59e0b',borderRadius:10,padding:'1px 7px',whiteSpace:'nowrap'}}>🏆 Winner</span>;
const RemarksCell = ({ s }) => (
  <td style={{fontSize:11,maxWidth:220,whiteSpace:'normal'}} title={[s.nactet_remarks&&('NACTET: '+s.nactet_remarks), s.remarks].filter(Boolean).join('\n')}>
    {s.nactet_remarks&&<div><span style={{fontWeight:700,color:'#92400e'}}>NACTET:</span> {s.nactet_remarks}</div>}
    {s.remarks&&<div><span style={{fontWeight:700,color:'var(--accent)'}}>CRM:</span> {s.remarks}</div>}
    {!s.nactet_remarks&&!s.remarks&&'—'}
  </td>
);

async function fetchAll(mk) {
  const out = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await mk().range(from, from + 999);
    if (error) return { data: out, error };
    out.push(...(data || []));
    if (!data || data.length < 1000) break;
  }
  return { data: out, error: null };
}
const PACKAGES      = ['NDT Level 1','NDT Level 2','PGDCEM','Short Term','Other'];
const RESP_COLOR    = {
  'Not Called':       '#94a3b8',
  'Low Interest':     '#ef4444',
  'Medium Interest':  '#f59e0b',
  'High Interest':    '#3b82f6',
  'Demo Scheduled':   '#0ea5e9',
  'Demo Attended':    '#8b5cf6',
  'Demo No Show':     '#dc2626',
  'Converted':        '#16a34a',
  'Lost':             '#dc2626',
  'Call Not Attended':'#f97316',
  'Not Reachable':    '#6b7280',
  'Call Back Later':  '#f97316',
  'Wrong Number':     '#94a3b8',
};

const today  = () => new Date().toISOString().split('T')[0];
const fmtDate= d  => d ? new Date(d+'T00:00:00').toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const RBadge = ({r}) => (
  <span style={{
    background:`${RESP_COLOR[r]||'#94a3b8'}18`,
    color: RESP_COLOR[r]||'#94a3b8',
    border:`1px solid ${RESP_COLOR[r]||'#94a3b8'}44`,
    borderRadius:6, padding:'2px 8px', fontSize:10, fontWeight:700, whiteSpace:'nowrap'
  }}>{r||'Not Called'}</span>
);

// ═══════════════════════════════════════════════════════════════════
// ROOT COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function MarketingCRM() {
  const { user, canView } = useAuth();
  const [profile,  setProfile]  = useState(null);
  const [isAdmin,  setIsAdmin]  = useState(false);
  const [dueCount, setDueCount] = useState(0);
  const [demoDueCount, setDemoDueCount] = useState(0);
  const [tab,      setTab]      = useState('dashboard');
  const [loading,  setLoading]  = useState(true);
  // Data year — every tab shows/saves only this year's college & student data
  const [year, setYear] = useState(()=>{
    try { const v = Number(localStorage.getItem(YEAR_KEY)); if (v >= 2021 && v <= THIS_YEAR + 1) return v; } catch(e) {}
    return THIS_YEAR;
  });
  const changeYear = (v) => { const n = Number(v); setYear(n); try { localStorage.setItem(YEAR_KEY, String(n)); } catch(e) {} };

  const ADMIN_EMAILS = ['ershadarmstech@gmail.com','armstechac@gmail.com'];
  useEffect(()=>{
    // count demos due today or past
    const fetchDemoDue = async () => {
      if (!profile && !isAdmin) return;
      let dq = supabase.from('crm_students').select('demo_scheduled_date,response').eq('response','Demo Scheduled').eq('academic_year', year);
      if (!isAdmin && profile?.id) dq = dq.eq('assigned_to', profile.id);
      const {data} = await dq;
      const t = today();
      setDemoDueCount((data||[]).filter(s=>s.demo_scheduled_date&&s.demo_scheduled_date<=t).length);
    };
    fetchDemoDue();
  },[year, profile, isAdmin]);

  useEffect(()=>{
    if (!user?.email) return;
    const admin = ADMIN_EMAILS.includes(user.email);
    setIsAdmin(admin);
    if (admin) {
      // Admin doesn't need staff profile
      setProfile({ staff_name: 'Admin', id: null });
      setLoading(false);
    } else {
      supabase.from('hr_staff').select('id,staff_name,personal_email,official_email')
        .or(`personal_email.eq.${user.email},official_email.eq.${user.email}`).maybeSingle()
        .then(({data})=>{ setProfile(data); setLoading(false); });
    }
  },[user]);

  useEffect(()=>{
    if (!profile && !isAdmin) return;
    let q = supabase.from('crm_students')
      .select('id',{count:'exact',head:true})
      .eq('academic_year', year)
      .lte('next_followup_date', today())
      .not('response','in','("Converted","Lost")');
    if (!isAdmin && profile) q = q.eq('assigned_to', profile.id);
    q.then(({count})=>setDueCount(count||0));
  },[profile,isAdmin,year]);

  if (loading) return <div style={{padding:40,textAlign:'center',color:'var(--text3)'}}>Loading...</div>;

  if (!isAdmin && !profile?.id) return (
    <div className="page">
      <div style={{padding:60,textAlign:'center'}}>
        <Megaphone size={48} style={{color:'var(--text3)',marginBottom:16}}/>
        <h2 style={{color:'var(--text2)'}}>Profile Not Linked</h2>
        <p style={{color:'var(--text3)'}}>Your login email (<strong>{user?.email}</strong>) is not linked to any staff profile.</p>
        <p style={{color:'var(--text3)'}}>Please ask admin to add your email in <strong>HR → Staff Details → Email field</strong>.</p>
      </div>
    </div>
  );

  const TABS = [
    { key:'dashboard', label:'🏠 Dashboard',   pkey:'crm_students'  },
    { key:'students',  label:'👥 Students',     pkey:'crm_students'  },
    { key:'calls',     label:'📞 Call Center',  pkey:'crm_calls',    badge: demoDueCount },
    { key:'reports',   label:'📊 Reports',      pkey:'crm_reports'   },
    { key:'followups', label:'📅 Follow-ups',   pkey:'crm_followups' },
    ...(isAdmin ? [{ key:'admin', label:'⚙️ Admin Panel', pkey:'crm_admin' }] : []),
  ].filter(t => canView(t.pkey));



  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Marketing CRM</h1>
          <p className="page-subtitle">Student outreach · Follow-ups · Demo & Admission tracking</p>
        </div>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          {dueCount>0&&(
            <button onClick={()=>setTab('calls')} style={{
              background:'#fef3c7',border:'1.5px solid #f59e0b',borderRadius:8,
              padding:'6px 14px',fontSize:12,fontWeight:700,color:'#92400e',
              cursor:'pointer',display:'flex',alignItems:'center',gap:6}}>
              <Bell size={13}/> {dueCount} Follow-up{dueCount>1?'s':''} Due
            </button>
          )}
          <div style={{display:'flex',alignItems:'center',gap:6,background:'var(--bg3)',padding:'4px 10px',borderRadius:8,border:'1.5px solid var(--accent)'}}>
            <span style={{fontSize:12,fontWeight:700,color:'var(--accent)'}}>📅 Data Year</span>
            <select value={year} onChange={e=>changeYear(e.target.value)} style={{fontSize:13,fontWeight:700,padding:'2px 6px',width:80}}>
              {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{fontSize:12,color:'var(--text3)',background:'var(--bg3)',padding:'6px 12px',borderRadius:8}}>
            👤 {profile?.staff_name || 'Admin'} {isAdmin&&<span style={{color:'var(--accent)',marginLeft:4}}>(Admin)</span>}
          </div>
        </div>
      </div>

      <div className="tabs" style={{marginBottom:20}}>
        {TABS.map(t=>(
          <div key={t.key} className={`tab ${tab===t.key?'active':''}`} onClick={()=>setTab(t.key)} style={{position:'relative'}}>
            {t.label}
            {t.badge>0&&<span style={{position:'absolute',top:-6,right:-6,background:'#dc2626',color:'#fff',borderRadius:'50%',width:16,height:16,fontSize:9,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center'}}>{t.badge}</span>}
          </div>
        ))}
      </div>

      {tab==='dashboard' && <Dashboard key={year} year={year} profile={profile} isAdmin={isAdmin} onGo={setTab}/>}
      {tab==='students'  && <Students key={year} year={year} profile={profile} isAdmin={isAdmin}/>}
      {tab==='calls'     && <CallCenter key={year} year={year} profile={profile} isAdmin={isAdmin}/>}
      {tab==='reports'   && <Reports key={year} year={year} profile={profile} isAdmin={isAdmin}/>}
      {tab==='followups' && <FollowupTracker key={year} year={year} profile={profile} isAdmin={isAdmin}/>}
      {tab==='admin' && isAdmin && <AdminPanel key={year} year={year}/>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 1. DASHBOARD
// ═══════════════════════════════════════════════════════════════════
function Dashboard({ year, profile, isAdmin, onGo }) {
  const [stats,   setStats]   = useState(null);
  const [dueList, setDueList] = useState([]);
  const [drill,   setDrill]   = useState(null);
  const [allStudents, setAllStudents] = useState([]);

  useEffect(()=>{ load(); },[profile?.id, isAdmin, year]);

  async function load() {
    const {data:all, error} = await fetchAll(()=>{
      let q = supabase.from('crm_students').select('id,response,next_followup_date,next_followup_type,full_name,college_name,stream,phone,whatsapp')
        .eq('academic_year', year).order('id');
      if (!isAdmin && profile) q = q.eq('assigned_to', profile.id);
      return q;
    });
    if (error) toast.error('Dashboard load failed: '+error.message);
    const students = all || [];

    const byR = {};
    RESPONSES.forEach(r=>byR[r]=0);
    students.forEach(s=>{ const r=s.response||'Not Called'; byR[r]=(byR[r]||0)+1; });

    setAllStudents(students);
    setStats({
      total:      students.length,
      notCalled:  byR['Not Called']||0,
      inProgress: (byR['Low Interest']||0)+(byR['Medium Interest']||0)+(byR['High Interest']||0)+(byR['Call Back Later']||0),
      demo:       byR['Demo Attended']||0,
      converted:  byR['Converted']||0,
      lost:       (byR['Lost']||0)+(byR['Wrong Number']||0),
      byR,
    });

    const due = students.filter(s=>s.next_followup_date&&s.next_followup_date<=today()&&s.response!=='Converted'&&s.response!=='Lost');
    setDueList(due.sort((a,b)=>a.next_followup_date.localeCompare(b.next_followup_date)));
  }

  if (!stats) return <div style={{padding:40,textAlign:'center',color:'var(--text3)'}}><RefreshCw size={20} style={{animation:'spin 1s linear infinite',marginBottom:8}}/><br/>Loading dashboard...</div>;

  return (<>
    <div>
      {/* Progress pipeline */}
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:'16px 20px',marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:13,marginBottom:14,color:'var(--text2)'}}>📊 Student Pipeline</div>
        <div style={{display:'flex',gap:0,alignItems:'stretch'}}>
          {[
            {label:'Total',        value:stats.total,     color:'#6b7280', bg:'#f1f5f9', filter: s=>true},
            {label:'Not Called',   value:stats.notCalled, color:'#94a3b8', bg:'#f8fafc', filter: s=>(s.response||'Not Called')==='Not Called'},
            {label:'In Progress',  value:stats.inProgress,color:'#f59e0b', bg:'#fffbeb', filter: s=>['Low Interest','Medium Interest','High Interest','Call Back Later'].includes(s.response)},
            {label:'Demo Done',    value:stats.demo,      color:'#8b5cf6', bg:'#fdf4ff', filter: s=>s.response==='Demo Attended'},
            {label:'✅ Converted', value:stats.converted, color:'#16a34a', bg:'#f0fdf4', filter: s=>s.response==='Converted'},
            {label:'❌ Lost',      value:stats.lost,      color:'#ef4444', bg:'#fef2f2', filter: s=>['Lost','Wrong Number'].includes(s.response)},
          ].map((s,i,arr)=>(
            <React.Fragment key={s.label}>
              <div onClick={()=>s.value>0&&setDrill({title:s.label+' ('+s.value+')',students:allStudents.filter(s.filter)})} style={{flex:1,background:s.bg,borderRadius:i===0?'10px 0 0 10px':i===arr.length-1?'0 10px 10px 0':0,padding:'14px 10px',textAlign:'center',cursor:s.value>0?'pointer':'default',border:`1px solid ${s.color}22`}}>
                <div style={{fontSize:26,fontWeight:900,color:s.color}}>{s.value}</div>
                <div style={{fontSize:10,color:s.color,fontWeight:600,marginTop:3}}>{s.label}</div>
                {stats.total>0&&<div style={{fontSize:9,color:'#94a3b8',marginTop:2}}>{Math.round(s.value/stats.total*100)}%</div>}
              </div>
              {i<arr.length-1&&<div style={{width:1,background:'var(--border)'}}/>}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        {/* Follow-ups due */}
        <div style={{background:'var(--surface)',border:'1.5px solid #f59e0b',borderRadius:12,overflow:'hidden'}}>
          <div style={{background:'#fffbeb',padding:'10px 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontWeight:700,fontSize:13,color:'#92400e'}}>⏰ Follow-ups Due ({dueList.length})</span>
            <button className="btn btn-secondary btn-sm" onClick={()=>onGo('calls')}>Open Call Center</button>
          </div>
          <div style={{maxHeight:280,overflowY:'auto'}}>
            {dueList.length===0&&<div style={{padding:24,textAlign:'center',color:'var(--text3)',fontSize:13}}>✅ All clear! No pending follow-ups.</div>}
            {dueList.map((s,i)=>(
              <div key={i} style={{padding:'10px 16px',borderBottom:'1px solid var(--border)',display:'flex',gap:10,alignItems:'center'}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:13}}>{s.full_name}</div>
                  <div style={{fontSize:11,color:'var(--text3)'}}>{s.college_name} · <span style={{color:s.stream==='Civil'?'#d97706':'#0891b2',fontWeight:600}}>{s.stream==='Civil'?'🏗️':'⚙️'} {s.stream}</span></div>
                  <div style={{fontSize:11,color:'#f59e0b',fontWeight:600,marginTop:2}}>📅 {fmtDate(s.next_followup_date)} · {s.next_followup_type||'Call'}</div>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:4,alignItems:'flex-end'}}>
                  <RBadge r={s.response}/>
                  {s.phone&&<a href={`whatsapp://send?phone=91${s.phone.replace(/[^0-9]/g,'')}`}
                    style={{fontSize:10,color:'#16a34a',textDecoration:'none',background:'#f0fdf4',padding:'2px 6px',borderRadius:4}}>💬 WA</a>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Response breakdown */}
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
          <div style={{background:'var(--bg3)',padding:'10px 16px',fontWeight:700,fontSize:13,color:'var(--text2)'}}>📈 Response Breakdown</div>
          <div style={{padding:'12px 16px',display:'flex',flexDirection:'column',gap:6}}>
            {RESPONSES.filter(r=>stats.byR[r]>0).map(r=>(
              <div key={r} onClick={()=>setDrill({title:r+' ('+stats.byR[r]+')',students:allStudents.filter(s=>(s.response||'Not Called')===r)})} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'2px 4px',borderRadius:6}} onMouseEnter={e=>e.currentTarget.style.background='var(--bg3)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{width:100,flexShrink:0}}><RBadge r={r}/></div>
                <div style={{flex:1,background:'#f1f5f9',borderRadius:4,height:16,overflow:'hidden'}}>
                  <div style={{width:`${stats.total>0?Math.round(stats.byR[r]/stats.total*100):0}%`,background:RESP_COLOR[r]||'#94a3b8',height:'100%',borderRadius:4,minWidth:4,transition:'width 0.5s'}}/>
                </div>
                <div style={{width:32,textAlign:'right',fontWeight:700,fontSize:12,color:RESP_COLOR[r]||'#94a3b8'}}>{stats.byR[r]}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {drill&&(
      <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setDrill(null)} style={{zIndex:1000}}>
        <div className="modal" style={{maxWidth:720}}>
          <div className="modal-header">
            <h3>📊 {drill.title}</h3>
            <button className="modal-close" onClick={()=>setDrill(null)}>×</button>
          </div>
          <div className="modal-body" style={{padding:0}}>
            <div className="table-wrap" style={{maxHeight:'60vh',overflowY:'auto'}}>
              <table>
                <thead><tr><th>#</th><th>Name</th><th>College</th><th>Stream</th><th>Phone</th><th>Response</th><th>Follow-up</th></tr></thead>
                <tbody>
                  {drill.students.length===0&&<tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No students</td></tr>}
                  {drill.students.map((s,i)=>(
                    <tr key={i}>
                      <td style={{fontSize:11,color:'var(--text3)'}}>{i+1}</td>
                      <td style={{fontWeight:600,fontSize:13}}>{s.full_name||'—'}</td>
                      <td style={{fontSize:11,maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.college_name||'—'}</td>
                      <td><span style={{fontSize:10,padding:'1px 5px',borderRadius:3,background:s.stream==='Civil'?'#fef3c7':'#e0f2fe',fontWeight:700}}>{s.stream||'—'}</span></td>
                      <td style={{fontSize:12}}><div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontFamily:'monospace',fontSize:11}}>{s.phone||'—'}</span>{s.phone&&<CallBtn phone={s.phone}/>}{s.whatsapp&&<WABtn phone={s.whatsapp}/>}</div></td>
                      <td><RBadge r={s.response}/></td>
                      <td style={{fontSize:11,color:s.next_followup_date&&s.next_followup_date<=today()?'#f59e0b':'var(--text3)',fontWeight:s.next_followup_date&&s.next_followup_date<=today()?700:400}}>{s.next_followup_date?fmtDate(s.next_followup_date):'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <span style={{fontSize:12,color:'var(--text3)'}}>{drill.students.length} students</span>
            <button className="btn" onClick={()=>setDrill(null)}>Close</button>
          </div>
        </div>
      </div>
    )}
  </> );
}

// ═══════════════════════════════════════════════════════════════════
// 2. STUDENTS — College-wise grouped view + individual list
// ═══════════════════════════════════════════════════════════════════
function StudentCallHistory({studentId}) {
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(()=>{
    supabase.from('crm_call_logs')
      .select('*,hr_staff:called_by(staff_name)')
      .eq('student_id', studentId)
      .order('call_date',{ascending:false})
      .then(({data})=>{ setLogs(data||[]); setLoading(false); });
  },[studentId]);
  if (loading) return <div style={{textAlign:'center',padding:16,color:'var(--text3)'}}>Loading call history...</div>;
  if (!logs.length) return <div style={{textAlign:'center',padding:16,color:'var(--text3)'}}>No call history yet</div>;
  return (
    <div style={{marginTop:10,borderTop:'1px solid var(--border)',paddingTop:8}}>
      <div style={{fontWeight:600,fontSize:12,marginBottom:6,color:'var(--text2)'}}>📞 Call History ({logs.length})</div>
      <div style={{display:'flex',flexDirection:'column',gap:3,maxHeight:200,overflowY:'auto',paddingRight:2}}>
        {logs.map(log=>(
          <div key={log.id} style={{background:'var(--bg3)',borderRadius:6,padding:'5px 8px',fontSize:11,borderLeft:`3px solid ${log.response==='Converted'?'#16a34a':log.response==='Not Interested'?'#dc2626':'#f59e0b'}`}}>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              <span style={{fontWeight:700,color:'var(--accent)',flexShrink:0}}>{log.response}</span>
              <span style={{color:'var(--text3)',flexShrink:0}}>{fmtDMY(log.call_date)}</span>
              {log.call_time&&<span style={{color:'var(--text3)',flexShrink:0}}>{log.call_time}</span>}
              <span style={{color:'var(--text3)',marginLeft:'auto',flexShrink:0,fontSize:10}}>{log.hr_staff?.staff_name||'—'}</span>
            </div>
            {log.notes&&<div style={{color:'var(--text)',marginTop:2,wordBreak:'break-word'}}>{log.notes}</div>}
            {log.next_followup_date&&<div style={{color:'#f59e0b',fontSize:10,marginTop:1}}>↪ {fmtDMY(log.next_followup_date)} · {log.next_followup_type}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Students({ year, profile, isAdmin }) {
  const [showView,   setShowView]   = useState(null);
  const [view,       setView]       = useState('colleges'); // 'colleges' | 'list'
  const [colleges,   setColleges]   = useState([]);  // master colleges
  const [students,   setStudents]   = useState([]);
  const [selected,   setSelected]   = useState(new Set());
  const [expanded,   setExpanded]   = useState({});
  const [showModal,  setShowModal]  = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showCall,   setShowCall]   = useState(null);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [impCollege, setImpCollege] = useState('');
  const [impStream,  setImpStream]  = useState('Civil');
  const [search,     setSearch]     = useState('');
  const [fStream,    setFStream]    = useState('');
  const [fResponse,  setFResponse]  = useState('');
  const [fYear,      setFYear]      = useState('');
  const [fCentre,    setFCentre]    = useState('');
  const [fCollege,   setFCollege]   = useState('');
  const fileRef = useRef();

  const emptyForm = {
    full_name:'', college_name:'', stream:'Civil', phone:'', whatsapp:'',
    email:'', year_of_passing:'', scholarship_marks:'', source:'NACTET Scholarship',
    response:'Not Called', next_followup_date:'', next_followup_type:'Call',
    centre:'', batch_no:'', package_name:'', admission_date:'', register_number:'', remarks:'', demo_date:'', demo_centre:'',
  };
  const [form, setForm] = useState(emptyForm);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));
  // Open the edit form with every field filled (nulls from the database become '', blank passing-year defaults to the data year)
  const openEditStudent = (s) => {
    const clean = {};
    Object.keys(s).forEach(k => { clean[k] = s[k] == null ? (emptyForm[k] ?? '') : s[k]; });
    setEditing(s.id);
    setForm({
      ...emptyForm, ...clean,
      year_of_passing: s.year_of_passing ? String(s.year_of_passing) : String(s.academic_year || year),
      scholarship_marks: s.scholarship_marks != null ? String(s.scholarship_marks) : '',
      demo_date: s.demo_scheduled_date || '', demo_centre: s.demo_centre || '',
    });
    setShowModal(true);
  };
  const toggleSel = (id) => setSelected(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  const toggleAll = () => setSelected(prev => prev.size===filtered.length ? new Set() : new Set(filtered.map(s=>s.id)));
  const toggleGrp = (ids, checked) => setSelected(prev => { const n=new Set(prev); ids.forEach(id => checked ? n.add(id) : n.delete(id)); return n; });

  // Load master colleges on mount — independent of profile/isAdmin
  useEffect(()=>{
    supabase.from('colleges').select('id,college_name,district,university').order('college_name')
      .then(({data,error})=>{
        if(error){ console.error('colleges load error:',error.message); return; }
        setColleges(data||[]);
      });
  },[]);

  useEffect(()=>{ load(); },[profile?.id, isAdmin, year]);

  async function load() {
    // Load students of the selected data year (all pages, not just first 1000)
    const {data:stds, error} = await fetchAll(()=>{
      let sq = supabase.from('crm_students').select('*').eq('academic_year', year)
        .order('college_name').order('scholarship_marks',{ascending:false,nullsFirst:false}).order('full_name').order('id');
      if (!isAdmin && profile?.id) sq = sq.eq('assigned_to', profile.id);
      return sq;
    });
    if(error) { console.error('students load error:',error); toast.error('Students load failed: '+error.message); }
    setStudents(stds||[]);
  }

  async function save() {
    if (!form.full_name.trim()) { toast.error('Student name required'); return; }
    setSaving(true);
    const { demo_date: _dd, demo_centre: _dc, enrolled_batch: _eb, enrolled_date: _ed, call_date: _cd, call_time: _ct, notes: _n, next_followup_type: _nft, ...formFields } = form;
    // Editing keeps the existing assignment (admin has no staff id — must not blank it)
    const payload = {...formFields, academic_year: Number(form.academic_year)||year,
      assigned_to: editing ? (form.assigned_to ?? profile?.id ?? null) : (profile?.id ?? null),
      scholarship_marks: form.scholarship_marks ? Number(form.scholarship_marks) : null };
    const {error} = editing
      ? await supabase.from('crm_students').update(payload).eq('id',editing)
      : await supabase.from('crm_students').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success(editing?'Updated ✅':'Student added ✅'); setShowModal(false); load(); }
    setSaving(false);
  }

  // Import Excel — college-wise
  async function handleImport(e) {
    if (!impCollege) { toast.error('Please select a college first'); return; }
    const file = e.target.files?.[0]; if (!file) return;
    const buf  = await file.arrayBuffer();
    const wb   = XLSX.read(buf,{type:'array'});
    const raw  = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]],{header:1,defval:''});
    if (raw.length<2) { toast.error('File empty'); return; }
    const hdrs = raw[0].map(h=>String(h||'').toLowerCase().trim());
    const getByHeader = (row, exactHeader) => {
      const i = hdrs.findIndex(h => h === exactHeader.toLowerCase().trim());
      return i >= 0 ? String(row[i]||'').trim() : '';
    };
    const rows = raw.slice(1).filter(r=>r.some(v=>v!==''&&v!=null));
    const toInsert = rows.map(row=>({
      full_name:        getByHeader(row, 'student name'),
      college_name:     getByHeader(row, 'college name') || impCollege,
      stream:           getByHeader(row, 'stream (civil/mechanical)') || getByHeader(row, 'stream') || impStream,
      phone:            getByHeader(row, 'phone'),
      whatsapp:         getByHeader(row, 'whatsapp'),
      email:            getByHeader(row, 'email'),
      year_of_passing:  getByHeader(row, 'year of passing'),
      scholarship_marks: Number(getByHeader(row, 'scholarship marks'))||null,
      source:           getByHeader(row, 'source') || 'NACTET Scholarship',
      response:         'Not Called',
      assigned_to:      profile?.id ?? null,
      academic_year:    year,
    })).filter(r=>r.full_name);
    if (!toInsert.length) { toast.error('No valid rows found'); fileRef.current.value=''; return; }
    // Check for duplicates by phone in same college before inserting
    const phones = toInsert.map(r=>r.phone).filter(Boolean);
    // Duplicates are checked within the SAME college AND SAME data year only
    const existingPhones = new Set();
    for (let i=0;i<phones.length;i+=100) {
      const {data:ex} = await supabase.from('crm_students')
        .select('phone').in('phone', phones.slice(i,i+100)).eq('college_name', impCollege).eq('academic_year', year);
      (ex||[]).forEach(x=>existingPhones.add(x.phone));
    }
    const duplicates = toInsert.filter(r=>r.phone&&existingPhones.has(r.phone));
    const newStudents = toInsert.filter(r=>!(r.phone&&existingPhones.has(r.phone)));
    if (duplicates.length > 0 && newStudents.length === 0) {
      toast.error(`All ${duplicates.length} students already exist in ${impCollege}. Import cancelled.`);
      fileRef.current.value=''; return;
    }
    if (duplicates.length > 0) {
      const ok = window.confirm(`⚠️ ${duplicates.length} students already exist in ${impCollege} and will be skipped:\n${duplicates.map(r=>r.full_name).join(', ')}\n\nProceed to import ${newStudents.length} new students?`);
      if (!ok) { fileRef.current.value=''; return; }
    }
    const toActuallyInsert = newStudents;
    const {error} = await supabase.from('crm_students').insert(toActuallyInsert);
    if (error) toast.error('Import failed: '+error.message);
    else { toast.success(`✅ ${toActuallyInsert.length} students imported into ${impCollege} (${impStream})${duplicates.length>0?' · '+duplicates.length+' duplicates skipped':''}`); load(); setShowImport(false); }
    fileRef.current.value='';
  }

  // Filter
  const filtered = students.filter(s=>{
    const q = search.toLowerCase();
    return (!q||s.full_name?.toLowerCase().includes(q)||s.phone?.includes(q)||s.college_name?.toLowerCase().includes(q))
      &&(!fStream  ||s.stream===fStream)
      &&(!fResponse||s.response===fResponse)
      &&(!fYear    ||s.year_of_passing===String(fYear))
      &&(!fCentre  ||s.centre===fCentre)
      &&(!fCollege ||s.college_name===fCollege);
  });

  // Group by college+stream for college view
  const collegeGroups = {};
  filtered.forEach(s=>{
    const key = `${s.college_name}||${s.stream}`;
    if (!collegeGroups[key]) collegeGroups[key]={college:s.college_name,stream:s.stream,students:[]};
    collegeGroups[key].students.push(s);
  });
  const groupKeys = Object.keys(collegeGroups).sort();

  return (
    <div>
      {/* Top bar */}
      <div style={{display:'flex',gap:6,marginBottom:16,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
          {/* View toggle */}
          <div style={{display:'flex',border:'1px solid var(--border)',borderRadius:8,overflow:'hidden'}}>
            <button onClick={()=>setView('colleges')} style={{padding:'5px 14px',border:'none',cursor:'pointer',fontSize:12,fontWeight:600,background:view==='colleges'?'var(--accent)':'var(--bg3)',color:view==='colleges'?'#fff':'var(--text2)'}}>🎓 By College</button>
            <button onClick={()=>setView('list')} style={{padding:'5px 14px',border:'none',cursor:'pointer',fontSize:12,fontWeight:600,background:view==='list'?'var(--accent)':'var(--bg3)',color:view==='list'?'#fff':'var(--text2)'}}>📋 All Students</button>
          </div>
          <div className="search-bar" style={{minWidth:200}}><Search/><input placeholder="Search name, phone, college..." value={search} onChange={e=>setSearch(e.target.value)}/></div>
          <select style={{width:200,fontSize:12,padding:'4px 6px'}} value={fCollege} onChange={e=>setFCollege(e.target.value)}>
            <option value="">— All Colleges —</option>
            {[...new Set(students.map(s=>s.college_name).filter(Boolean))].sort().map(name=>
              <option key={name} value={name}>{name}</option>
            )}
          </select>
          <select style={{width:100,fontSize:12,padding:'4px 6px'}} value={fStream} onChange={e=>setFStream(e.target.value)}>
            <option value="">All Streams</option>
            <option value="Civil">🏗️ Civil</option>
            <option value="Mechanical">⚙️ Mechanical</option>
          </select>
          <select style={{width:130,fontSize:12,padding:'4px 6px'}} value={fResponse} onChange={e=>setFResponse(e.target.value)}>
            <option value="">All Responses</option>
            {RESPONSES.map(r=><option key={r} value={r}>{r}</option>)}
          </select>
          <select style={{width:80,fontSize:12,padding:'4px 6px'}} value={fYear} onChange={e=>setFYear(e.target.value)}>
            <option value="">Passing Yr</option>
            {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <select style={{width:80,fontSize:12,padding:'4px 6px'}} value={fCentre} onChange={e=>setFCentre(e.target.value)}>
            <option value="">Centre</option>
            {CENTRES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          {(search||fCollege||fStream||fResponse||fYear||fCentre)&&
            <button className="btn btn-ghost btn-sm" onClick={()=>{setSearch('');setFCollege('');setFStream('');setFResponse('');setFYear('');setFCentre('');}}>✕ Clear</button>}
          <span style={{fontSize:12,color:'var(--text3)'}}>{filtered.length} students</span>
        </div>
        <div style={{display:'flex',gap:6}}>
          <button className="btn btn-secondary" onClick={()=>setShowImport(true)}><Upload size={14}/> Import by College</button>
          <button className="btn btn-primary" onClick={()=>{setEditing(null);setForm(emptyForm);setShowModal(true);}}><Plus size={14}/> Add Student</button>
        </div>
      </div>

      {/* ── COLLEGE VIEW ── */}
      {view==='colleges' && (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {groupKeys.length===0&&<div className="empty-state"><Building2 size={32}/><h3>No students found</h3><p>Import student data or add individually</p></div>}
          {groupKeys.map(key=>{
            const grp = collegeGroups[key];
            const isOpen = expanded[key];
            const byR = {};
            grp.students.forEach(s=>{ const r=s.response||'Not Called'; byR[r]=(byR[r]||0)+1; });
            const converted   = byR['Converted']||0;
            const highInt     = (byR['High Interest']||0)+(byR['Demo Attended']||0);
            const due         = grp.students.filter(s=>s.next_followup_date&&s.next_followup_date<=today()&&s.response!=='Converted'&&s.response!=='Lost').length;
            const notCalled   = grp.students.filter(s=>!s.response||s.response==='Not Called').length;
            const isCivil     = grp.stream==='Civil';
            return (
              <div key={key} style={{border:'1px solid var(--border)',borderRadius:12,overflow:'hidden',background:'var(--surface)'}}>
                {/* College header */}
                <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 18px',cursor:'pointer',
                  background:isOpen?'var(--accent-light)':'var(--surface)',
                  borderLeft:`4px solid ${isCivil?'#d97706':'#0891b2'}`}}
                  onClick={()=>setExpanded(e=>({...e,[key]:!e[key]}))}>
                  <span style={{color:'var(--accent)'}}>{isOpen?<ChevronDown size={16}/>:<ChevronRight size={16}/>}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <span style={{fontWeight:800,fontSize:15}}>{grp.college}</span>
                      <span style={{
                        background:isCivil?'#fef3c7':'#e0f2fe',
                        color:isCivil?'#92400e':'#0369a1',
                        borderRadius:6,padding:'2px 10px',fontSize:11,fontWeight:700
                      }}>{isCivil?'🏗️ Civil':'⚙️ Mechanical'}</span>
                      <span style={{background:'var(--bg3)',borderRadius:6,padding:'2px 8px',fontSize:11,color:'var(--text3)'}}>{grp.students.length} students</span>
                    </div>
                    {/* Mini progress bar */}
                    <div style={{display:'flex',gap:8,marginTop:6,alignItems:'center'}}>
                      <div style={{flex:1,background:'#f1f5f9',borderRadius:4,height:6,overflow:'hidden',display:'flex'}}>
                        {RESPONSES.map(r=>byR[r]>0&&(
                          <div key={r} style={{width:`${byR[r]/grp.students.length*100}%`,background:RESP_COLOR[r]||'#94a3b8',height:'100%'}} title={`${r}: ${byR[r]}`}/>
                        ))}
                      </div>
                      <div style={{display:'flex',gap:8,fontSize:11}}>
                        {notCalled>0&&<span style={{color:'#94a3b8'}}>Not called: {notCalled}</span>}
                        {highInt>0&&<span style={{color:'#3b82f6',fontWeight:600}}>⭐ {highInt}</span>}
                        {due>0&&<span style={{color:'#f59e0b',fontWeight:700}}>⏰ {due} due</span>}
                        {converted>0&&<span style={{color:'#16a34a',fontWeight:700}}>✅ {converted}</span>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded student list */}
                {isOpen&&(
                  <div>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th><input type="checkbox" onChange={e=>toggleGrp(grp.students.map(s=>s.id),e.target.checked)}/></th>
                            <th>#</th><th>Name</th><th>Phone</th><th>WhatsApp</th>
                            <th>Marks</th><th>Source</th><th>Remarks</th>
                            <th>Response</th><th>Next Follow-up</th>
                            <th>Centre</th><th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...grp.students].sort((a,b)=>(Number(b.scholarship_marks)||0)-(Number(a.scholarship_marks)||0)).map((s,i)=>{
                            const isDue = s.next_followup_date&&s.next_followup_date<=today()&&s.response!=='Converted'&&s.response!=='Lost';
                            return (
                              <tr key={s.id} style={{background:isDue?'rgba(245,158,11,0.06)':s.response==='Converted'?'rgba(22,163,74,0.04)':undefined}}>
                                <td><input type="checkbox" checked={selected.has(s.id)} onChange={()=>toggleSel(s.id)} style={{width:13,height:13}}/></td>
                                <td style={{fontSize:11,color:'var(--text3)'}}>{i+1}</td>
                                <td style={{fontWeight:600,fontSize:13,minWidth:120}}>{s.full_name}{isWinner(s)&&<WinnerTag/>}</td>
                                <td>
                                  <div style={{display:'flex',alignItems:'center',gap:4}}>
                                    <span style={{fontSize:12,fontFamily:'monospace'}}>{s.phone||'—'}</span>
                                    {s.phone&&<a href={`tel:${s.phone.replace(/[^0-9+]/g,'')}`} style={{fontSize:11,color:'var(--accent)',textDecoration:'none'}}>📞 {s.phone}</a>}
                                  </div>
                                </td>
                                <td>
                                  {s.whatsapp||s.phone
                                    ? <a href={`whatsapp://send?phone=91${(s.whatsapp||s.phone).replace(/[^0-9]/g,'')}`}
                                        style={{fontSize:11,color:'#16a34a',textDecoration:'none',background:'#f0fdf4',padding:'2px 6px',borderRadius:4}}>💬 WhatsApp</a>
                                    : '—'}
                                </td>
                                <td style={{textAlign:'center',fontWeight:700,fontSize:13,color:'var(--accent)'}}>{s.scholarship_marks||'—'}</td>
                                <td style={{fontSize:11}}>{s.source||'—'}</td>
                                <RemarksCell s={s}/>
                                <td><RBadge r={s.response}/>{s.demo_scheduled_date&&<div style={{fontSize:9,color:'#0369a1',marginTop:2}}>📅 {fmtDate(s.demo_scheduled_date)}{s.demo_centre?` · ${s.demo_centre}`:''}</div>}</td>
                                <td style={{fontSize:11,whiteSpace:'nowrap',color:isDue?'#f59e0b':undefined,fontWeight:isDue?700:undefined}}>
                                  {s.next_followup_date?fmtDate(s.next_followup_date):'—'}
                                  {isDue&&<div style={{fontSize:9,color:'#f59e0b'}}>⏰ DUE</div>}
                                </td>
                                <td>{s.centre?<span className="badge badge-blue" style={{fontSize:9}}>{s.centre}</span>:'—'}</td>
                                <td>
                                  <div style={{display:'flex',gap:3}}>
                                    <button className="btn btn-primary btn-sm" title="Log Call" onClick={()=>setShowCall(s)} style={{fontSize:11}}>📞 Call</button>
                                    <button className="btn btn-ghost btn-sm" onClick={()=>setShowView(s)} title="View"><Eye size={12}/></button>
                                    <button className="btn btn-ghost btn-sm" onClick={()=>openEditStudent(s)}><Edit2 size={12}/></button>
                                    <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(!window.confirm('Delete?'))return;await supabase.from('crm_students').delete().eq('id',s.id);load();}}><Trash2 size={12}/></button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── ALL STUDENTS LIST VIEW ── */}
      {view==='list' && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th><input type="checkbox" checked={selected.size===filtered.length&&filtered.length>0} onChange={toggleAll} style={{width:14,height:14}}/></th>
                <th>#</th><th>Name</th><th>College</th><th>Stream</th><th>Phone</th><th>Marks</th><th>Remarks</th><th>Response</th><th>Next Follow-up</th><th>Centre</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length===0&&<tr><td colSpan={12}><div className="empty-state"><Users size={32}/><h3>No students found</h3></div></td></tr>}
              {filtered.map((s,i)=>{
                const isDue = s.next_followup_date&&s.next_followup_date<=today()&&s.response!=='Converted'&&s.response!=='Lost';
                return (
                  <tr key={s.id} style={{background:isDue?'rgba(245,158,11,0.06)':s.response==='Converted'?'rgba(22,163,74,0.04)':undefined}}>
                    <td><input type="checkbox" checked={selected.has(s.id)} onChange={()=>toggleSel(s.id)} style={{width:14,height:14}}/></td>
                    <td style={{fontSize:11,color:'var(--text3)'}}>{i+1}</td>
                    <td style={{fontWeight:600,fontSize:13}}>{s.full_name}{isWinner(s)&&<WinnerTag/>}</td>
                    <td style={{fontSize:12,maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.college_name||'—'}</td>
                    <td><span style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:s.stream==='Civil'?'#fef3c7':'#e0f2fe',color:s.stream==='Civil'?'#92400e':'#0369a1',fontWeight:700}}>{s.stream==='Civil'?'🏗️':'⚙️'} {s.stream}</span></td>
                    <td>
                      {s.phone?<div style={{display:'flex',gap:4,alignItems:'center'}}>
                        <span style={{fontSize:12,fontFamily:'monospace'}}>{s.phone}</span>
                        <a href={`whatsapp://send?phone=91${(s.whatsapp||s.phone).replace(/[^0-9]/g,'')}`} style={{fontSize:10,color:'#16a34a',textDecoration:'none'}}>💬</a>
                      </div>:'—'}
                    </td>
                    <td style={{textAlign:'center',fontWeight:700,color:'var(--accent)'}}>{s.scholarship_marks||'—'}</td>
                    <RemarksCell s={s}/>
                    <td><RBadge r={s.response}/>{s.demo_scheduled_date&&<div style={{fontSize:9,color:'#0369a1',marginTop:2}}>📅 {fmtDate(s.demo_scheduled_date)}{s.demo_centre?` · ${s.demo_centre}`:''}</div>}</td>
                    <td style={{fontSize:11,color:isDue?'#f59e0b':undefined,fontWeight:isDue?700:undefined}}>{s.next_followup_date?fmtDate(s.next_followup_date):'—'}</td>
                    <td>{s.centre?<span className="badge badge-blue" style={{fontSize:9}}>{s.centre}</span>:'—'}</td>
                    <td><div style={{display:'flex',gap:3}}>
                      <button className="btn btn-primary btn-sm" style={{fontSize:11}} onClick={()=>setShowCall(s)}>📞 Call</button>
                      <button className="btn btn-ghost btn-sm" onClick={()=>setShowView(s)} title="View"><Eye size={12}/></button>
                      <button className="btn btn-ghost btn-sm" onClick={()=>openEditStudent(s)}><Edit2 size={12}/></button>
                      <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(!window.confirm('Delete?'))return;await supabase.from('crm_students').delete().eq('id',s.id);load();}}><Trash2 size={12}/></button>
                    </div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── ADD/EDIT STUDENT MODAL ── */}
      {showModal&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal modal-lg" style={{maxWidth:680,maxHeight:'92vh',overflowY:'auto'}}>
            <div className="modal-header">
              <span className="modal-title">{editing?'Edit':'Add'} Student</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{fontWeight:700,fontSize:12,color:'var(--text3)',marginBottom:10}}>A — Student Details</div>
              <div className="form-grid">
                <div className="form-group full"><label>Full Name *</label><input value={form.full_name} onChange={e=>sf('full_name',e.target.value)} placeholder="Student full name"/></div>
                <div className="form-group"><label>College Name</label>
                  <select value={form.college_name} onChange={e=>sf('college_name',e.target.value)}>
                    <option value="">— Select College —</option>
                    {form.college_name && !colleges.find(c=>c.college_name===form.college_name) &&
                      <option value={form.college_name}>{form.college_name}</option>
                    }
                    {colleges.length>0
                      ? colleges.map(col=><option key={col.id} value={col.college_name}>{col.college_name}{col.district?' — '+col.district:''}</option>)
                      : <option disabled value="">⚠️ No colleges found — check Master Data → Colleges</option>
                    }
                  </select>
                </div>
                <div className="form-group"><label>Stream</label>
                  <select value={form.stream} onChange={e=>sf('stream',e.target.value)} style={{borderColor:form.stream==='Civil'?'#d97706':'#0891b2'}}>
                    <option value="Civil">🏗️ Civil</option>
                    <option value="Mechanical">⚙️ Mechanical</option>
                    {['Automobile','Aeronautical','Electrical','Other'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Phone Number</label><input value={form.phone} onChange={e=>sf('phone',e.target.value)} placeholder="Mobile number"/></div>
                <div className="form-group"><label>WhatsApp Number</label><input value={form.whatsapp} onChange={e=>sf('whatsapp',e.target.value)} placeholder="If different from phone"/></div>
                <div className="form-group"><label>Email ID</label><input type="email" value={form.email} onChange={e=>sf('email',e.target.value)}/></div>
                <div className="form-group"><label>Year of Passing</label>
                  <select value={form.year_of_passing} onChange={e=>sf('year_of_passing',e.target.value)}>
                    <option value="">Select Year</option>{YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Scholarship Exam Marks</label><input type="number" value={form.scholarship_marks} onChange={e=>sf('scholarship_marks',e.target.value)} placeholder="Marks scored"/></div>
                <div className="form-group"><label>Source</label>
                  <select value={form.source} onChange={e=>sf('source',e.target.value)}>{SOURCES.map(s=><option key={s} value={s}>{s}</option>)}</select>
                </div>
                <div className="form-group"><label>Centre</label>
                  <select value={form.centre} onChange={e=>sf('centre',e.target.value)}>
                    <option value="">— Select —</option>
                    {CENTRES.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div style={{fontWeight:700,fontSize:12,color:'var(--text3)',margin:'14px 0 10px'}}>B — Current Status</div>
              <div className="form-grid">
                <div className="form-group"><label>Response</label>
                  <select value={form.response} onChange={e=>sf('response',e.target.value)} style={{borderColor:RESP_COLOR[form.response]||'var(--border)'}}>{RESPONSES.map(r=><option key={r} value={r}>{r}</option>)}</select>
                </div>
                <div className="form-group"><label>Next Follow-up Date</label><DateInput value={form.next_followup_date} onChange={e=>sf('next_followup_date',e.target.value)}/></div>
                <div className="form-group"><label>Follow-up Type</label>
                  <select value={form.next_followup_type} onChange={e=>sf('next_followup_type',e.target.value)}>{FOLLOWUP_TYPES.map(t=><option key={t} value={t}>{t}</option>)}</select>
                </div>
                {form.response==='Demo Scheduled'&&(
                  <><div className="form-group"><label>Demo Scheduled Date *</label><DateInput value={form.demo_date||''} onChange={e=>sf('demo_date',e.target.value)}/></div>
                  <div className="form-group"><label>Demo Centre *</label>
                    <select value={form.demo_centre||''} onChange={e=>sf('demo_centre',e.target.value)}>
                      <option value=''>Select Centre</option>
                      <option value='EKM'>EKM</option>
                      <option value='TVM'>TVM</option>
                    </select>
                  </div></>
                )}
                {(form.response==='Demo Attended'||form.response==='Converted')&&(
                  <div className="form-group"><label>Centre *</label>
                    <select value={form.centre} onChange={e=>sf('centre',e.target.value)} style={{borderColor:'var(--accent)'}}>
                      <option value="">— Select Centre —</option>
                      {CENTRES.map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
              </div>
              {form.response==='Converted'&&(
                <div style={{background:'#f0fdf4',border:'1.5px solid #86efac',borderRadius:10,padding:'14px 16px',marginTop:10}}>
                  <div style={{fontWeight:700,fontSize:12,color:'#15803d',marginBottom:10}}>✅ Admission / Conversion Details</div>
                  <div className="form-grid">
                    <div className="form-group"><label>Centre</label><select value={form.centre} onChange={e=>sf('centre',e.target.value)}><option value="">Select</option>{CENTRES.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
                    <div className="form-group"><label>Package Name</label>
                      <select value={form.package_name} onChange={e=>sf('package_name',e.target.value)}>
                        <option value="">Select Package</option>{PACKAGES.map(p=><option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div className="form-group"><label>Batch Number</label><input value={form.batch_no} onChange={e=>sf('batch_no',e.target.value)}/></div>
                    <div className="form-group"><label>Admission Date</label><DateInput value={form.admission_date} onChange={e=>sf('admission_date',e.target.value)}/></div>
                    <div className="form-group"><label>Register Number</label><input value={form.register_number} onChange={e=>sf('register_number',e.target.value)}/></div>
                  </div>
                </div>
              )}
              {form.nactet_remarks&&<div className="form-group" style={{marginTop:10}}><label>NACTET Remarks <span style={{fontWeight:400,color:'var(--text3)'}}>(from NACTET Scholarship — edit there)</span></label><textarea rows={2} value={form.nactet_remarks} readOnly style={{background:'var(--bg3)'}}/></div>}
              <div className="form-group" style={{marginTop:10}}><label>Remarks <span style={{fontWeight:400,color:'var(--text3)'}}>(CRM notes)</span></label><textarea rows={2} value={form.remarks||''} onChange={e=>sf('remarks',e.target.value)}/></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':editing?'Update':'Add Student'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── IMPORT MODAL ── */}
      {showImport&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowImport(false)}>
          <div className="modal" style={{maxWidth:540}}>
            <div className="modal-header"><span className="modal-title">📥 Import Students — College Wise</span><button className="btn btn-ghost btn-sm" onClick={()=>setShowImport(false)}><X size={16}/></button></div>
            <div className="modal-body">
              <div style={{background:'#eff6ff',border:'1px solid #93c5fd',borderRadius:10,padding:'12px 16px',marginBottom:16}}>
                <div style={{fontWeight:700,marginBottom:8,color:'#1d4ed8'}}>Step 1 — Select College & Stream</div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>College Name *</label>
                    <select value={impCollege} onChange={e=>setImpCollege(e.target.value)}>
                      <option value="">— Select College —</option>
                      {colleges.length>0
                        ? colleges.map(col=><option key={col.id} value={col.college_name}>{col.college_name}{col.district?' — '+col.district:''}</option>)
                        : <option disabled value="">⚠️ No colleges found — check Master Data → Colleges</option>
                      }
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Stream *</label>
                    <select value={impStream} onChange={e=>setImpStream(e.target.value)} style={{borderColor:impStream==='Civil'?'#d97706':'#0891b2',fontWeight:700}}>
                      <option value="Civil">🏗️ Civil</option>
                      <option value="Mechanical">⚙️ Mechanical</option>
                    </select>
                  </div>
                </div>
              </div>
              <div style={{background:'var(--accent-light)',border:'1px solid var(--accent)',borderRadius:10,padding:'12px 16px',marginBottom:16}}>
                <div style={{fontWeight:700,marginBottom:8}}>Step 2 — Supported Column Names (flexible)</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4,fontSize:12}}>
                  {[['Name / Student Name','Phone / Contact / Mob'],['WhatsApp / WA','Email / Mail'],['Year / Passing / Batch','Mark / Score / Rank'],['Source','(others auto-filled)']].map(([a,b],i)=>(
                    <React.Fragment key={i}>
                      <div style={{background:'rgba(255,255,255,0.5)',borderRadius:4,padding:'3px 8px'}}>{a}</div>
                      <div style={{background:'rgba(255,255,255,0.5)',borderRadius:4,padding:'3px 8px'}}>{b}</div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <div style={{border:'2px dashed var(--border)',borderRadius:10,padding:24,textAlign:'center',cursor:'pointer',opacity:impCollege?1:0.5}} onClick={()=>impCollege&&fileRef.current?.click()}>
                <Upload size={28} style={{color:'var(--accent)',marginBottom:6}}/>
                <div style={{fontWeight:600,fontSize:13}}>{impCollege?`Upload Excel for ${impCollege} (${impStream})`:'Select college first, then upload'}</div>
                <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>Supports .xlsx, .xls, .csv</div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={handleImport}/>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setShowImport(false)}>Close</button></div>
          </div>
        </div>
      )}

      {showCall&&<CallLogModal student={showCall} profile={profile} onClose={()=>setShowCall(null)} onSaved={load}/>}

      {showView&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowView(null)}>
          <div className="modal" style={{maxWidth:600}}>
            <div className="modal-header">
              <h3>👤 {showView.full_name}</h3>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <button title="Reset all status, demo, enrollment and call history back to Not Called"
                  style={{fontSize:11,padding:'4px 10px',borderRadius:6,border:'1px solid var(--red)',color:'var(--red)',background:'none',cursor:'pointer',fontWeight:600}}
                  onClick={async()=>{
                    if(!window.confirm(`Reset ${showView.full_name} completely?\n\nThis will clear:\n- Response (back to "Not Called")\n- Demo scheduled date/centre\n- Enrollment details\n- Follow-up date\n- ALL call history\n\nThis cannot be undone.`))return;
                    await supabase.from('crm_call_logs').delete().eq('student_id',showView.id);
                    const { error: resetErr } = await supabase.from('crm_students').update({
                      response:'Not Called', demo_scheduled_date:null, demo_centre:null, demo_reschedule_count:0,
                      batch_no:null, admission_date:null, next_followup_date:null, next_followup_type:null,
                    }).eq('id',showView.id);
                    if (resetErr) { toast.error('Reset failed: '+resetErr.message); return; }
                    await supabase.from('crm_call_logs').delete().eq('student_id',showView.id);
                    toast.success(`${showView.full_name} reset to Not Called`);
                    setShowView(null); await load();
                  }}>🔄 Reset Status</button>
                <button className="modal-close" onClick={()=>setShowView(null)}>×</button>
              </div>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
                {[
                  ['College',showView.college_name],
                  ['Stream',showView.stream],
                  ['Phone',showView.phone],
                  ['WhatsApp',showView.whatsapp],
                  ['Email',showView.email],
                  ['Year of Passing',showView.year_of_passing],
                  ['Scholarship Marks',showView.scholarship_marks],
                  ['Source',showView.source],
                  ['Response',showView.response],
                  ['Centre',showView.centre],
                ].map(([l,v])=>v?(
                  <div key={l} style={{fontSize:13}}>
                    <span style={{color:'var(--text3)',fontSize:11}}>{l}</span>
                    <div style={{fontWeight:500}}>{v}</div>
                  </div>
                ):null)}
              </div>
              <StudentCallHistory studentId={showView.id}/>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={()=>setShowView(null)}>Close</button>
              <button className="btn btn-primary" onClick={()=>{setShowCall(showView);setShowView(null);}}>📞 Log Call</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CALL LOG MODAL
// ═══════════════════════════════════════════════════════════════════
function CallLogModal({ student, profile, onClose, onSaved }) {
  const [logs,   setLogs]   = useState([]);
  const [saving, setSaving] = useState(false);
  const [form,   setForm]   = useState({
    call_date:today(), call_time:new Date().toTimeString().slice(0,5), response:student.response||'Not Called', demo_date:student.demo_scheduled_date||'', demo_centre:student.demo_centre||'', enrolled_batch:'', enrolled_date:'',
    notes:'', next_followup_date:'', next_followup_type:'Call',
  });
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(()=>{ loadLogs(); },[]);
  async function loadLogs() {



    const {data} = await supabase.from('crm_call_logs')
      .select('*,hr_staff:called_by(staff_name)')
      .eq('student_id',student.id).order('call_date',{ascending:false});
    setLogs(data||[]);
  }

  async function save() {
    if (!form.response) { toast.error('Select response'); return; }
    setSaving(true);
    await supabase.from('crm_call_logs').insert({
      demo_scheduled_date: form.demo_date||null,
      demo_centre:         form.demo_centre||null,
      enrolled_batch:     form.enrolled_batch||null,
      enrolled_date:      form.enrolled_date||null,
      student_id:         student.id,
      called_by:          profile?.id,
      call_date:          form.call_date,
      call_time:          form.call_time||null,
      response:           form.response,
      notes:              form.notes,
      next_followup_date: form.next_followup_date||null,
      next_followup_type: form.next_followup_type,
    });
    await supabase.from('crm_students').update({
      response:             form.response,
      next_followup_date:   form.next_followup_date||null,
      next_followup_type:   form.next_followup_type,
      ...(form.response==='Demo Scheduled' ? {demo_scheduled_date:form.demo_date||null, demo_centre:form.demo_centre||null} : {}),
    }).eq('id',student.id);
    toast.success('Call logged ✅');
    setSaving(false); loadLogs(); onSaved();
    setForm({call_date:today(),call_time:new Date().toTimeString().slice(0,5),response:form.response,notes:'',next_followup_date:'',next_followup_type:'Call',demo_date:'',demo_centre:'',enrolled_batch:'',enrolled_date:''});
  }

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-lg" style={{maxWidth:660,maxHeight:'92vh',overflowY:'auto'}}>
        <div className="modal-header">
          <div>
            <div className="modal-title">📞 {student.full_name}</div>
            <div style={{fontSize:12,color:'rgba(255,255,255,0.8)',marginTop:2}}>
              {student.college_name} · {student.stream==='Civil'?'🏗️':'⚙️'} {student.stream} · <PhoneField phone={student.phone}/>
              {student.demo_scheduled_date&&<span style={{marginLeft:8,fontSize:11,background:'#e0f2fe',color:'#0369a1',borderRadius:4,padding:'1px 6px',fontWeight:600}}>📅 Demo: {fmtDate(student.demo_scheduled_date)}{student.demo_centre?` (${student.demo_centre})`:''}</span>}
              {student.scholarship_marks&&<span> · Marks: {student.scholarship_marks}</span>}
            </div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            {student.phone&&<a href={`whatsapp://send?phone=91${(student.whatsapp||student.phone).replace(/[^0-9]/g,'')}`} style={{background:'rgba(255,255,255,0.2)',color:'#fff',borderRadius:6,padding:'4px 10px',fontSize:12,textDecoration:'none'}}>💬 WA</a>}
            <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
          </div>
        </div>
        <div className="modal-body">
          {/* New call */}
          <div style={{background:'var(--accent-light)',border:'1.5px solid var(--accent)',borderRadius:10,padding:'14px 16px',marginBottom:16}}>
            <div style={{fontWeight:700,fontSize:13,color:'var(--accent)',marginBottom:12}}>➕ Log This Call</div>
            <div className="form-grid">
              <div className="form-group"><label>Date *</label><DateInput value={form.call_date} onChange={e=>sf('call_date',e.target.value)}/></div>
              <div className="form-group"><label>Time</label><input type="time" value={form.call_time} onChange={e=>sf('call_time',e.target.value)}/></div>
              <div className="form-group"><label>Response *</label>
                <select value={form.response} onChange={e=>sf('response',e.target.value)} style={{borderColor:RESP_COLOR[form.response]||'var(--border)',fontWeight:700}}>
                  {RESPONSES.map(r=><option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Next Follow-up Date</label><DateInput value={form.next_followup_date} onChange={e=>sf('next_followup_date',e.target.value)}/></div>
              <div className="form-group"><label>Follow-up Type</label>
                <select value={form.next_followup_type} onChange={e=>sf('next_followup_type',e.target.value)}>{FOLLOWUP_TYPES.map(t=><option key={t} value={t}>{t}</option>)}</select>
              </div>
              {form.response==='Demo Scheduled'&&<>
                <div className="form-group"><label>Demo Scheduled Date *</label><DateInput value={form.demo_date||''} onChange={e=>sf('demo_date',e.target.value)}/></div>
                <div className="form-group"><label>Demo Centre *</label>
                  <select value={form.demo_centre||''} onChange={e=>sf('demo_centre',e.target.value)}>
                    <option value=''>Select Centre</option>
                    <option value='EKM'>EKM</option>
                    <option value='TVM'>TVM</option>
                  </select>
                </div>
              </>}
              {form.response==='Demo Attended'&&<>
                <div className="form-group"><label>Demo Date</label><DateInput value={form.demo_date||''} onChange={e=>sf('demo_date',e.target.value)}/></div>
                <div className="form-group"><label>Demo Centre</label>
                  <select value={form.demo_centre||''} onChange={e=>sf('demo_centre',e.target.value)}>
                    <option value=''>Select Centre</option>
                    <option value='EKM'>EKM</option>
                    <option value='TVM'>TVM</option>
                  </select>
                </div>
              </>}
              {form.response==='Converted'&&<>
                <div className="form-group"><label>Batch Name</label><input value={form.enrolled_batch||''} onChange={e=>sf('enrolled_batch',e.target.value)} placeholder="e.g. EM48, EC14"/></div>
                <div className="form-group"><label>Enrolled Date</label><DateInput value={form.enrolled_date||''} onChange={e=>sf('enrolled_date',e.target.value)}/></div>
              </>}
              <div className="form-group full"><label>Notes (what did the student say?)</label>
                <textarea rows={2} value={form.notes} onChange={e=>sf('notes',e.target.value)} placeholder="Student's response, interest level, any specific info..."/>
              </div>
            </div>
            <div style={{textAlign:'right',marginTop:8}}>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save Call Log'}</button>
            </div>
          </div>

          {/* Call history */}
          <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:'var(--text2)'}}>📋 Call History ({logs.length} calls)</div>
          {logs.length===0&&<div style={{padding:20,textAlign:'center',color:'var(--text3)',background:'var(--bg3)',borderRadius:8,fontSize:13}}>No calls logged yet for this student.</div>}
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {logs.map((log,i)=>(
              <div key={log.id} style={{padding:'12px 14px',background:'var(--bg3)',borderRadius:8,border:'1px solid var(--border)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                  <div style={{display:'flex',gap:10,alignItems:'center'}}>
                    <span style={{fontWeight:700,fontSize:13}}>#{logs.length-i} — {fmtDate(log.call_date)}</span>
                    {log.call_time&&<span style={{fontSize:11,color:'var(--text3)'}}>🕐 {log.call_time}</span>}
                    <RBadge r={log.response}/>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:11,color:'var(--text3)'}}>by {log.hr_staff?.staff_name||'—'}</span>
                    <button title="Delete this call log entry" onClick={async()=>{
                      if(!window.confirm('Delete this call history entry? This cannot be undone.'))return;
                      await supabase.from('crm_call_logs').delete().eq('id',log.id);
                      toast.success('Call log deleted');
                      loadLogs();
  // Real-time updates (debounced)
  useEffect(() => {
    let _rt = null;
    const _reload = () => { clearTimeout(_rt); _rt = setTimeout(() => { try { load(); } catch(e){} }, 800); };
    const ch = supabase.channel('marketingcrm-realtime')
      .on('postgres_changes', {event:'*', schema:'public', table:'crm_students'}, () => _reload())
      .on('postgres_changes', {event:'*', schema:'public', table:'crm_call_logs'}, () => _reload())
      .subscribe();
    return () => { clearTimeout(_rt); supabase.removeChannel(ch); };
  }, []);

                    }} style={{background:'none',border:'none',cursor:'pointer',color:'var(--red)',padding:2,display:'flex',alignItems:'center'}}>
                      <Trash2 size={13}/>
                    </button>
                  </div>
                </div>
                {log.notes&&<div style={{fontSize:12,color:'var(--text2)',padding:'6px 8px',background:'var(--surface)',borderRadius:6,marginBottom:6,wordBreak:'break-word',whiteSpace:'pre-wrap',overflowWrap:'break-word'}}>{log.notes}</div>}
                {log.next_followup_date&&(
                  <div style={{fontSize:11,color:'var(--accent)',fontWeight:600}}>
                    📅 Follow-up: {fmtDate(log.next_followup_date)} via {log.next_followup_type||'Call'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 3. CALL CENTER
// ═══════════════════════════════════════════════════════════════════
function CallCenter({ year, profile, isAdmin }) {
  const [students, setStudents] = useState([]);
  const [showCall, setShowCall] = useState(null);
  const [showView, setShowView] = useState(null);
  const [showReschedule, setShowReschedule] = useState(null);
  const [fStream,  setFStream]  = useState('');
  const [fType,    setFType]    = useState('');
  const [search,   setSearch]   = useState('');

  useEffect(()=>{ load(); },[profile,isAdmin,year]);
  async function load() {
    const {data,error} = await fetchAll(()=>{
      let q = supabase.from('crm_students').select('*,demo_scheduled_date,demo_centre,demo_reschedule_count')
        .eq('academic_year', year)
        .not('response','eq','Converted')
        .not('response','eq','Lost')
        .not('response','eq','Wrong Number')
        .order('next_followup_date', {ascending:true,nullsFirst:false}).order('id');
      if (!isAdmin && profile?.id) q = q.eq('assigned_to', profile.id);
      return q;
    });
    if (error) { console.error('CallCenter load error:', error); toast.error('Load failed: '+error.message); }
    setStudents(data||[]);
  }

  const filtered = students.filter(s=>
    (!search||s.full_name?.toLowerCase().includes(search.toLowerCase())||s.phone?.includes(search))
    &&(!fStream||s.stream===fStream)
    &&(!fType  ||s.next_followup_type===fType)
  );
  const overdue      = filtered.filter(s=>s.next_followup_date && s.next_followup_date<today());
  const dueToday     = filtered.filter(s=>s.next_followup_date && s.next_followup_date===today());
  const upcoming     = filtered.filter(s=>s.next_followup_date && s.next_followup_date>today());
  const noDate       = filtered.filter(s=>!s.next_followup_date);
  const demoAll      = students.filter(s=>s.response==='Demo Scheduled');
  const demoPast     = demoAll.filter(s=>s.demo_scheduled_date && s.demo_scheduled_date<today());
  const demoToday2   = demoAll.filter(s=>s.demo_scheduled_date && s.demo_scheduled_date===today());
  const demoUpcoming = demoAll.filter(s=>s.demo_scheduled_date && s.demo_scheduled_date>today());
  async function markAttended(s){
    await supabase.from('crm_students').update({response:'Demo Attended'}).eq('id',s.id);
    await supabase.from('crm_call_logs').insert({student_id:s.id,called_by:profile?.id,call_date:today(),call_time:new Date().toTimeString().slice(0,5),response:'Demo Attended',notes:'Demo attended ✅'});
    toast.success(s.full_name+' — Demo Attended ✅'); load();
  }
  async function markNoShow(s){
    await supabase.from('crm_students').update({response:'Demo No Show',next_followup_date:today(),next_followup_type:'Call'}).eq('id',s.id);
    await supabase.from('crm_call_logs').insert({student_id:s.id,called_by:profile?.id,call_date:today(),call_time:new Date().toTimeString().slice(0,5),response:'Demo No Show',notes:'Did not attend demo ❌'});
    toast.error(s.full_name+' — No Show ❌'); load();
  }
  async function doReschedule(s, newDate, newCentre){
    if (!newDate || !newCentre) { toast.error('Please select date and centre'); return; }
    const newCount = (s.demo_reschedule_count||0) + 1;
    await supabase.from('crm_students').update({
      response: 'Demo Scheduled',
      demo_scheduled_date: newDate,
      demo_centre: newCentre,
      demo_reschedule_count: newCount,
    }).eq('id', s.id);
    await supabase.from('crm_call_logs').insert({
      student_id: s.id, called_by: profile?.id, call_date: today(), call_time: new Date().toTimeString().slice(0,5),
      response: 'Demo Scheduled', demo_scheduled_date: newDate, demo_centre: newCentre,
      notes: `Demo rescheduled (attempt #${newCount+1}) after no-show`,
    });
    toast.success(`${s.full_name} — Demo rescheduled to ${fmtDate(newDate)}`);
    setShowReschedule(null); load();
  }

  const Card = ({s}) => (
    <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,padding:'14px',display:'flex',flexDirection:'column',gap:8}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
        <div>
          <div style={{fontWeight:700,fontSize:14}}>{s.full_name}</div>
          <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>{s.college_name}</div>
          <div style={{fontSize:11,marginTop:2}}>
            <span style={{background:s.stream==='Civil'?'#fef3c7':'#e0f2fe',color:s.stream==='Civil'?'#92400e':'#0369a1',borderRadius:4,padding:'1px 6px',fontWeight:700,fontSize:10}}>
              {s.stream==='Civil'?'🏗️':'⚙️'} {s.stream}
            </span>
            {s.scholarship_marks&&<span style={{marginLeft:6,fontSize:11,color:'var(--accent)',fontWeight:600}}>Marks: {s.scholarship_marks}</span>}
          </div>
        </div>
        <RBadge r={s.response}/>
      </div>
      <div style={{fontFamily:'monospace',fontSize:13,fontWeight:600}}>{s.phone||'—'}</div>
      {s.demo_scheduled_date&&<div style={{fontSize:11,color:'#0369a1',fontWeight:600,background:'#e0f2fe',borderRadius:4,padding:'2px 8px',display:'inline-block'}}>📅 Demo: {fmtDate(s.demo_scheduled_date)}{s.demo_centre?` · ${s.demo_centre}`:''}</div>}
      <div style={{fontSize:11,color:'#f59e0b',fontWeight:600}}>
        📅 {fmtDate(s.next_followup_date)} · {s.next_followup_type||'Call'}
      </div>
      <div style={{display:'flex',gap:6}}>
        {s.phone&&<a href={`whatsapp://send?phone=91${(s.whatsapp||s.phone).replace(/[^0-9]/g,'')}`}
          style={{flex:1,textAlign:'center',padding:'6px',background:'#f0fdf4',color:'#16a34a',borderRadius:6,fontSize:12,fontWeight:600,textDecoration:'none',border:'1px solid #86efac'}}>
          💬 WhatsApp
        </a>}
        <button className="btn btn-primary" style={{flex:1,fontSize:12}} onClick={()=>setShowCall(s)}>📞 Log Call</button>
      </div>
    </div>
  );

  const DemoCard = ({s,isPast}) => (
    <div style={{background:'var(--surface)',border:`1.5px solid ${isPast?'#dc2626':'#0ea5e9'}`,borderRadius:10,padding:'12px 14px',display:'flex',flexDirection:'column',gap:8}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8}}>
        <div>
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <div style={{fontWeight:700,fontSize:14}}>{s.full_name}</div>
            {s.demo_reschedule_count>0&&<span style={{fontSize:9,fontWeight:700,background:'#fef3c7',color:'#92400e',borderRadius:4,padding:'1px 6px'}}>🔄 Rescheduled {s.demo_reschedule_count}x</span>}
          </div>
          <div style={{fontSize:11,color:'var(--text3)'}}>{s.college_name} · {s.stream}</div>
          <div style={{fontSize:12,fontFamily:'monospace',marginTop:2}}>{s.phone||'—'}</div>
        </div>
        <div style={{textAlign:'right',flexShrink:0}}>
          <div style={{fontSize:10,fontWeight:700,color:isPast?'#dc2626':'#0ea5e9'}}>{isPast?'⚠️ Was on':'📅 Scheduled'}</div>
          <div style={{fontSize:13,fontWeight:700}}>{fmtDate(s.demo_scheduled_date)}</div>
          {s.demo_centre&&<span style={{fontSize:10,background:'#e0f2fe',color:'#0369a1',borderRadius:4,padding:'1px 6px',marginTop:2,display:'inline-block'}}>{s.demo_centre}</span>}
        </div>
      </div>
      <div style={{display:'flex',gap:6}}>
        <button onClick={()=>markAttended(s)} className="btn btn-primary btn-sm" style={{flex:1,background:'#16a34a',borderColor:'#16a34a',fontSize:11}}>✅ Attended</button>
        <button onClick={()=>markNoShow(s)} className="btn btn-secondary btn-sm" style={{flex:1,fontSize:11,color:'#dc2626',borderColor:'#dc2626'}}>❌ No Show</button>
        <button onClick={()=>setShowReschedule(s)} className="btn btn-secondary btn-sm" style={{flex:1,fontSize:11,color:'#d97706',borderColor:'#d97706'}}>🔄 Reschedule</button>
        <button onClick={()=>setShowCall(s)} className="btn btn-secondary btn-sm" style={{fontSize:11}}>📞</button>
      </div>
    </div>
  );

  const Section = ({title,items,color,bg}) => items.length===0?null:(
    <div style={{marginBottom:24}}>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
        <span style={{background:bg,color,border:`1px solid ${color}44`,borderRadius:8,padding:'4px 14px',fontWeight:700,fontSize:13}}>{title}</span>
        <span style={{fontSize:12,color:'var(--text3)'}}>{items.length} students</span>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:10}}>
        {items.map(s=><Card key={s.id} s={s}/>)}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{display:'flex',gap:6,marginBottom:16,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
          <div className="search-bar" style={{minWidth:200}}><Search/><input placeholder="Search name or phone..." value={search} onChange={e=>setSearch(e.target.value)}/></div>
          <select style={{width:110,fontSize:12,padding:'4px 6px'}} value={fStream} onChange={e=>setFStream(e.target.value)}>
            <option value="">All Streams</option>
            <option value="Civil">🏗️ Civil</option>
            <option value="Mechanical">⚙️ Mechanical</option>
          </select>
          <select style={{width:150,fontSize:12,padding:'4px 6px'}} value={fType} onChange={e=>setFType(e.target.value)}>
            <option value="">All Follow-up Types</option>
            {FOLLOWUP_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          {(search||fStream||fType)&&<button className="btn btn-ghost btn-sm" onClick={()=>{setSearch('');setFStream('');setFType('');}}>✕ Clear</button>}
          <span style={{fontSize:12,color:'var(--text3)'}}>{filtered.length} students · {overdue.length} overdue · {dueToday.length} today</span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13}/> Refresh</button>
      </div>
      {(demoPast.length>0||demoToday2.length>0||demoUpcoming.length>0)&&(
        <div style={{background:'var(--surface)',border:'1.5px solid #0ea5e9',borderRadius:12,overflow:'hidden',marginBottom:8}}>
          <div style={{padding:'10px 16px',background:'#e0f2fe',display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            <span style={{fontWeight:700,fontSize:13,color:'#0369a1'}}>🎯 Demo Tracker</span>
            {(demoPast.length+demoToday2.length)>0&&<span style={{background:'#dc2626',color:'#fff',borderRadius:8,padding:'2px 8px',fontSize:11,fontWeight:700}}>{demoPast.length+demoToday2.length} Need Action</span>}
            <span style={{marginLeft:'auto',fontSize:11,color:'#0369a1'}}>{demoUpcoming.length} upcoming</span>
          </div>
          <div style={{padding:'12px 16px',display:'flex',flexDirection:'column',gap:10}}>
            {demoPast.length>0&&<><div style={{fontSize:11,fontWeight:700,color:'#dc2626'}}>⚠️ PAST — Action Required ({demoPast.length})</div>{demoPast.map(s=><DemoCard key={s.id} s={s} isPast={true}/>)}</>}
            {demoToday2.length>0&&<><div style={{fontSize:11,fontWeight:700,color:'#0ea5e9'}}>📅 TODAY ({demoToday2.length})</div>{demoToday2.map(s=><DemoCard key={s.id} s={s} isPast={false}/>)}</>}
            {demoUpcoming.length>0&&<details><summary style={{cursor:'pointer',fontSize:12,color:'var(--text3)',padding:'4px 0',userSelect:'none'}}>▶ Upcoming ({demoUpcoming.length})</summary><div style={{display:'flex',flexDirection:'column',gap:8,marginTop:8}}>{demoUpcoming.map(s=><DemoCard key={s.id} s={s} isPast={false}/>)}</div></details>}
          </div>
        </div>
      )}
      {filtered.length===0&&<div className="empty-state"><CheckCircle size={40} style={{color:'var(--green)'}}/><h3>No students found 🎉</h3></div>}
      <Section title="⚠️ Overdue"        items={overdue}   color="#dc2626" bg="#fef2f2"/>
      <Section title="📅 Due Today"      items={dueToday}  color="#d97706" bg="#fffbeb"/>
      <Section title="📆 Upcoming"       items={upcoming}  color="#2563eb" bg="#eff6ff"/>
      <Section title="📋 Not Scheduled"  items={noDate}    color="#6b7280" bg="#f9fafb"/>
      {showCall&&<CallLogModal student={showCall} profile={profile} onClose={()=>setShowCall(null)} onSaved={load}/>}

      {showReschedule&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowReschedule(null)}>
          <div className="modal" style={{maxWidth:380}}>
            <div className="modal-header">
              <h3>🔄 Reschedule Demo</h3>
              <button className="btn-icon" onClick={()=>setShowReschedule(null)}>✕</button>
            </div>
            <div style={{padding:'16px',display:'flex',flexDirection:'column',gap:12}}>
              <div style={{fontSize:13,color:'var(--text2)'}}>
                <strong>{showReschedule.full_name}</strong> · {showReschedule.college_name}
                {showReschedule.demo_reschedule_count>0&&<div style={{marginTop:4,fontSize:11,color:'#92400e'}}>Already rescheduled {showReschedule.demo_reschedule_count} time(s) before</div>}
              </div>
              <div className="form-group"><label>New Demo Date *</label>
                <DateInput id="resched-date" defaultValue={showReschedule.demo_scheduled_date||''}/>
              </div>
              <div className="form-group"><label>Demo Centre *</label>
                <select id="resched-centre" defaultValue={showReschedule.demo_centre||''}>
                  <option value=''>Select Centre</option>
                  <option value='EKM'>EKM</option>
                  <option value='TVM'>TVM</option>
                </select>
              </div>
              <div style={{display:'flex',gap:8,marginTop:4}}>
                <button className="btn btn-secondary" style={{flex:1}} onClick={()=>setShowReschedule(null)}>Cancel</button>
                <button className="btn btn-primary" style={{flex:1,background:'#d97706',borderColor:'#d97706'}}
                  onClick={()=>{
                    const d = document.getElementById('resched-date').value;
                    const ce = document.getElementById('resched-centre').value;
                    doReschedule(showReschedule, d, ce);
                  }}>Confirm Reschedule</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showView&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowView(null)}>
          <div className="modal" style={{maxWidth:600}}>
            <div className="modal-header">
              <h3>👤 {showView.full_name}</h3>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <button title="Reset all status, demo, enrollment and call history back to Not Called"
                  style={{fontSize:11,padding:'4px 10px',borderRadius:6,border:'1px solid var(--red)',color:'var(--red)',background:'none',cursor:'pointer',fontWeight:600}}
                  onClick={async()=>{
                    if(!window.confirm(`Reset ${showView.full_name} completely?\n\nThis will clear:\n- Response (back to "Not Called")\n- Demo scheduled date/centre\n- Enrollment details\n- Follow-up date\n- ALL call history\n\nThis cannot be undone.`))return;
                    await supabase.from('crm_call_logs').delete().eq('student_id',showView.id);
                    const { error: resetErr } = await supabase.from('crm_students').update({
                      response:'Not Called', demo_scheduled_date:null, demo_centre:null, demo_reschedule_count:0,
                      batch_no:null, admission_date:null, next_followup_date:null, next_followup_type:null,
                    }).eq('id',showView.id);
                    if (resetErr) { toast.error('Reset failed: '+resetErr.message); return; }
                    await supabase.from('crm_call_logs').delete().eq('student_id',showView.id);
                    toast.success(`${showView.full_name} reset to Not Called`);
                    setShowView(null); await load();
                  }}>🔄 Reset Status</button>
                <button className="modal-close" onClick={()=>setShowView(null)}>×</button>
              </div>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
                {[
                  ['College',showView.college_name],
                  ['Stream',showView.stream],
                  ['Phone',showView.phone],
                  ['WhatsApp',showView.whatsapp],
                  ['Email',showView.email],
                  ['Year of Passing',showView.year_of_passing],
                  ['Scholarship Marks',showView.scholarship_marks],
                  ['Source',showView.source],
                  ['Response',showView.response],
                  ['Centre',showView.centre],
                ].map(([l,v])=>v?(
                  <div key={l} style={{fontSize:13}}>
                    <span style={{color:'var(--text3)',fontSize:11}}>{l}</span>
                    <div style={{fontWeight:500}}>{v}</div>
                  </div>
                ):null)}
              </div>
              <StudentCallHistory studentId={showView.id}/>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={()=>setShowView(null)}>Close</button>
              <button className="btn btn-primary" onClick={()=>{setShowCall(showView);setShowView(null);}}>📞 Log Call</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 4. REPORTS
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// FOLLOW-UP TRACKER
// ═══════════════════════════════════════════════════════════════════
function FollowupTracker({ year, profile, isAdmin }) {
  const pad = n => String(n).padStart(2,'0');
  const fmt = d => { if(!d)return'—'; const [y,m,dd]=d.split('-'); return `${dd}/${m}/${y}`; };
  const todayStr = () => { const d=new Date(); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; };

  // default: this week (Mon to Sun)
  const getThisWeek = () => {
    const d = new Date();
    const day = d.getDay(); // 0=Sun
    const mon = new Date(d); mon.setDate(d.getDate() - (day===0?6:day-1));
    const sun = new Date(mon); sun.setDate(mon.getDate()+6);
    const s = dt => `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}`;
    return { from: s(mon), to: s(sun) };
  };

  const [preset,   setPreset]   = useState('this_week');
  const [fromDate, setFromDate] = useState(getThisWeek().from);
  const [toDate,   setToDate]   = useState(getThisWeek().to);
  const [staffFilter, setStaffFilter] = useState('');
  const [responseFilter, setResponseFilter] = useState('');
  const [collegeFilter, setCollegeFilter] = useState('');
  const [streamFilter, setStreamFilter] = useState('');
  const [rows,     setRows]     = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [collegeList, setCollegeList] = useState([]);
  const [loading,  setLoading]  = useState(false);

  useEffect(()=>{
    fetchAll(()=>{
      let cq = supabase.from('crm_students').select('id,college_name').eq('academic_year', year).not('college_name','is',null).order('id');
      if (!isAdmin && profile?.id) cq = cq.eq('assigned_to', profile.id);
      return cq;
    }).then(({data})=>setCollegeList([...new Set((data||[]).map(x=>x.college_name).filter(Boolean))].sort()));
    Promise.all([
      supabase.from('staff').select('full_name,email').eq('is_active',true).eq('staff_category','academic_counsellor'),
      supabase.from('hr_staff').select('id,staff_name,personal_email,official_email').eq('is_active',true).order('staff_name'),
    ]).then(([{data:counsellors},{data:hrStaff}])=>{
      const names = new Set((counsellors||[]).map(s=>s.full_name?.toLowerCase()));
      const emails = new Set((counsellors||[]).map(s=>s.email?.toLowerCase()).filter(Boolean));
      const filtered = (hrStaff||[]).filter(s=>names.has(s.staff_name?.toLowerCase())||emails.has(s.personal_email?.toLowerCase())||emails.has(s.official_email?.toLowerCase()));
      const list = filtered.length>0 ? filtered : (hrStaff||[]);
      if (!isAdmin && profile?.email) {
        const mine = list.filter(s=>s.personal_email===profile.email||s.official_email===profile.email);
        setStaffList(mine.length>0 ? mine : list);
      } else { setStaffList(list); }
    });
  },[]);

  function applyPreset(p) {
    setPreset(p);
    const d = new Date();
    const s = dt => `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}`;
    if (p==='today') { const t=todayStr(); setFromDate(t); setToDate(t); }
    else if (p==='yesterday') { const y=new Date(d); y.setDate(d.getDate()-1); const ys=s(y); setFromDate(ys); setToDate(ys); }
    else if (p==='this_week') { const w=getThisWeek(); setFromDate(w.from); setToDate(w.to); }
    else if (p==='this_month') { setFromDate(`${d.getFullYear()}-${pad(d.getMonth()+1)}-01`); setToDate(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(new Date(d.getFullYear(),d.getMonth()+1,0).getDate())}`); }
    else if (p==='last_month') { const lm=new Date(d.getFullYear(),d.getMonth()-1,1); const le=new Date(d.getFullYear(),d.getMonth(),0); setFromDate(s(lm)); setToDate(s(le)); }
  }

  useEffect(()=>{ loadData(); },[fromDate, toDate, staffFilter, responseFilter, collegeFilter, streamFilter]);

  async function loadData() {
    if (!fromDate||!toDate) return;
    setLoading(true);
    let q = supabase.from('crm_call_logs')
      .select('id,call_date,call_time,response,next_followup_date,next_followup_type,notes,demo_scheduled_date,demo_centre,called_by,crm_students!inner(full_name,college_name,stream,phone,response,assigned_to,academic_year)')
      .eq('crm_students.academic_year', year)
      .gte('next_followup_date', fromDate)
      .lte('next_followup_date', toDate)
      .order('next_followup_date', {ascending:true})
      .order('call_date', {ascending:false});
    if (staffFilter)    q = q.eq('called_by', staffFilter);
    if (responseFilter) q = q.eq('response', responseFilter);
    // Non-admin: only show follow-ups for their assigned students
    if (!isAdmin && profile?.id) q = q.eq('crm_students.assigned_to', profile.id);
    // Note: college/stream filtered client-side below after fetch
    const {data,error} = await q;
    if (error) toast.error('Load failed: '+error.message);
    let result = data||[];
    if (collegeFilter) result = result.filter(r=>r.crm_students?.college_name===collegeFilter);
    if (streamFilter)  result = result.filter(r=>r.crm_students?.stream===streamFilter);
    setRows(result);
    setLoading(false);
  }

  const overdue  = rows.filter(r=>r.next_followup_date<todayStr());
  const dueToday = rows.filter(r=>r.next_followup_date===todayStr());
  const upcoming = rows.filter(r=>r.next_followup_date>todayStr());

  const PRESET_BTNS = [
    {key:'today',label:'Today'},{key:'yesterday',label:'Yesterday'},
    {key:'this_week',label:'This Week'},{key:'this_month',label:'This Month'},
    {key:'last_month',label:'Last Month'},{key:'custom',label:'Custom'},
  ];

  const Row = ({r, pastColor}) => (
    <tr key={r.id} style={{background:pastColor||undefined}}>
      <td style={{fontWeight:700,fontSize:12,color:r.next_followup_date<todayStr()?'#dc2626':r.next_followup_date===todayStr()?'#d97706':'var(--text)'}}>
        {fmt(r.next_followup_date)}<br/><span style={{fontSize:10,fontWeight:400,color:'var(--text3)'}}>{r.next_followup_type||'Call'}</span>
      </td>
      <td>
        <div style={{fontWeight:700,fontSize:13}}>{r.crm_students?.full_name||'—'}</div>
        <div style={{fontSize:11,color:'var(--text3)'}}>{r.crm_students?.college_name} · {r.crm_students?.stream}</div>
        <div style={{fontSize:11,fontFamily:'monospace'}}>{r.crm_students?.phone||''}</div>
      </td>
      <td style={{fontSize:11}}>
        <div>📞 Called: {fmt(r.call_date)}{r.call_time?' '+r.call_time:''}</div>
        <RBadge r={r.response}/>
        {r.notes&&<div style={{fontSize:11,color:'var(--text2)',marginTop:2,maxWidth:200,wordBreak:'break-word'}}>{r.notes}</div>}
      </td>
      <td style={{fontSize:11,color:'var(--text2)'}}>{r.crm_students?.response?<RBadge r={r.crm_students.response}/>:'—'}</td>
    </tr>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      {/* Filter bar */}
      <div style={{background:'var(--surface)',borderRadius:10,padding:'12px 16px',display:'flex',flexDirection:'column',gap:10}}>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {PRESET_BTNS.map(b=>(
            <button key={b.key} className={`btn btn-sm ${preset===b.key?'btn-primary':'btn-secondary'}`}
              onClick={()=>applyPreset(b.key)} style={{fontSize:12}}>{b.label}</button>
          ))}
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          <DateInput value={fromDate} onChange={e=>{setFromDate(e.target.value);setPreset('custom');}} style={{width:150,fontSize:12,padding:'4px 6px'}}/>
          <span style={{fontSize:12,color:'var(--text3)'}}>to</span>
          <DateInput value={toDate} onChange={e=>{setToDate(e.target.value);setPreset('custom');}} style={{width:150,fontSize:12,padding:'4px 6px'}}/>
          <select style={{width:160,fontSize:12,padding:'4px 6px'}} value={responseFilter} onChange={e=>setResponseFilter(e.target.value)}>
            <option value="">All Responses</option>
            {RESPONSES.map(r=><option key={r} value={r}>{r}</option>)}
          </select>
          <select style={{width:170,fontSize:12,padding:'4px 6px'}} value={collegeFilter} onChange={e=>setCollegeFilter(e.target.value)}>
            <option value="">All Colleges</option>
            {collegeList.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select style={{width:110,fontSize:12,padding:'4px 6px'}} value={streamFilter} onChange={e=>setStreamFilter(e.target.value)}>
            <option value="">All Streams</option>
            <option value="Mechanical">Mechanical</option>
            <option value="Civil">Civil</option>
          </select>
          {isAdmin&&<select style={{width:150,fontSize:12,padding:'4px 6px'}} value={staffFilter} onChange={e=>setStaffFilter(e.target.value)}>
            <option value="">All Users</option>
            {staffList.map(s=><option key={s.id} value={s.id}>{s.staff_name}</option>)}
          </select>}
          {!isAdmin&&staffList.length>0&&<select style={{width:150,fontSize:12,padding:'4px 6px'}} value={staffFilter} onChange={e=>setStaffFilter(e.target.value)}>
            <option value="">All My Follow-ups</option>
            {staffList.map(s=><option key={s.id} value={s.id}>{s.staff_name}</option>)}
          </select>}
          {(staffFilter||responseFilter||collegeFilter||streamFilter)&&<button className="btn btn-ghost btn-sm" onClick={()=>{setStaffFilter('');setResponseFilter('');setCollegeFilter('');setStreamFilter('');}}>✕ Clear</button>}
        </div>
        <div style={{display:'flex',gap:16,fontSize:12}}>
          <span style={{color:'#dc2626',fontWeight:700}}>⚠️ Overdue: {overdue.length}</span>
          <span style={{color:'#d97706',fontWeight:700}}>📅 Today: {dueToday.length}</span>
          <span style={{color:'var(--text3)'}}>🔜 Upcoming: {upcoming.length}</span>
          <span style={{color:'var(--text3)',marginLeft:'auto'}}>Total: {rows.length} follow-ups</span>
        </div>
      </div>

      {/* Table */}
      {loading ? <div style={{textAlign:'center',padding:40}}>Loading...</div> : rows.length===0 ?
        <div style={{textAlign:'center',padding:40,color:'var(--text3)'}}>No follow-ups scheduled in this date range</div> :
        <div style={{overflowX:'auto'}}>
          <table className="table" style={{fontSize:12}}>
            <thead><tr>
              <th style={{width:110}}>Follow-up Date</th>
              <th>Student</th>
              <th>Call Details</th>
              <th>Current Status</th>
            </tr></thead>
            <tbody>
              {overdue.length>0&&<tr><td colSpan={4} style={{background:'#fef2f2',color:'#dc2626',fontWeight:700,fontSize:11,padding:'6px 12px'}}>⚠️ OVERDUE ({overdue.length})</td></tr>}
              {overdue.map(r=><Row key={r.id} r={r}/>)}
              {dueToday.length>0&&<tr><td colSpan={4} style={{background:'#fffbeb',color:'#d97706',fontWeight:700,fontSize:11,padding:'6px 12px'}}>📅 TODAY ({dueToday.length})</td></tr>}
              {dueToday.map(r=><Row key={r.id} r={r}/>)}
              {upcoming.length>0&&<tr><td colSpan={4} style={{background:'#f0fdf4',color:'#16a34a',fontWeight:700,fontSize:11,padding:'6px 12px'}}>🔜 UPCOMING ({upcoming.length})</td></tr>}
              {upcoming.map(r=><Row key={r.id} r={r}/>)}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

function Reports({ year, profile, isAdmin }) {
  const [fromDate, setFromDate] = useState(today());
  const [toDate,   setToDate]   = useState(today());
  const [fStaff, setFStaff] = useState('');
  const [staffs, setStaffs] = useState([]);
  const [calls,  setCalls]  = useState([]);
  const [all,    setAll]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null); // {title, students}

  useEffect(()=>{ loadStaffs(); },[]);
  useEffect(()=>{ loadData(); },[fromDate,toDate,fStaff,isAdmin,profile,year]);

  async function loadStaffs() {
    // Get academic counsellors from staff table
    const {data: staffData} = await supabase.from('staff').select('id,full_name,email,staff_category').eq('is_active',true).eq('staff_category','academic_counsellor');
    // Match with hr_staff by email to get hr_staff.id (used in called_by)
    const {data: hrData} = await supabase.from('hr_staff').select('id,staff_name,personal_email,official_email').eq('is_active',true).order('staff_name');
    // Cross-reference or just use hr_staff directly filtered by name match
    const counselorNames = new Set((staffData||[]).map(s=>s.full_name?.toLowerCase()));
    const filtered = (hrData||[]).filter(s=>counselorNames.size===0||counselorNames.has(s.staff_name?.toLowerCase()));
    const allStaffs = filtered.length>0 ? filtered : (hrData||[]).map(s=>({id:s.id,staff_name:s.staff_name}));
    // Non-admin: only show their own name in dropdown
    if (!isAdmin && profile?.email) {
      const mine = allStaffs.filter(s=>s.personal_email===profile.email||s.official_email===profile.email||s.staff_name?.toLowerCase()===profile.full_name?.toLowerCase());
      setStaffs(mine.length>0 ? mine : allStaffs);
    } else {
      setStaffs(allStaffs);
    }
  }

  async function loadData() {
    setLoading(true);
    try {
      // Get hr_staff id for current user or selected staff
      let staffId = null;
      if (isAdmin && fStaff) {
        staffId = fStaff; // already hr_staff.id from dropdown
      } else if (!isAdmin && profile?.email) {
        const {data:hr} = await supabase.from('hr_staff').select('id').or(`personal_email.eq.${profile.email},official_email.eq.${profile.email}`).single();
        staffId = hr?.id || null;
      }

      // Load calls in date range
      let callQ = supabase.from('crm_call_logs')
        .select('*,crm_students!crm_call_logs_student_id_fkey!inner(id,full_name,college_name,stream,phone,centre,response,academic_year)')
        .eq('crm_students.academic_year', year)
        .gte('call_date', fromDate)
        .lte('call_date', toDate)
        .order('call_date',{ascending:false});
      if(staffId) callQ = callQ.eq('called_by', staffId);
      const {data:callData,error:e1} = await callQ;
      if(e1) throw e1;
      setCalls(callData||[]);

      // Load students for response breakdown - filter by staff if selected
      const {data:stuData,error:e2} = await fetchAll(()=>{
        let stuQ = supabase.from('crm_students')
          .select('id,full_name,college_name,stream,phone,whatsapp,response,centre,next_followup_date,next_followup_type,assigned_to')
          .eq('academic_year', year).order('id');
        if(staffId) stuQ = stuQ.eq('assigned_to', staffId);
        return stuQ;
      });
      if(e2) throw e2;
      setAll(stuData||[]);
    } catch(err) {
      toast.error('Report load failed: '+err.message);
    }
    setLoading(false);
  }

  const load = loadData;

  const byR = {};
  RESPONSES.forEach(r=>byR[r]=0);
  all.forEach(s=>{ const r=s.response||'Not Called'; byR[r]=(byR[r]||0)+1; });
  const converted     = byR['Converted']||0;
  const demo          = byR['Demo Attended']||0;
  const followupsDone = calls.filter(c=>c.next_followup_date).length;
  const dueToday      = all.filter(s=>s.next_followup_date===today()&&s.response!=='Converted'&&s.response!=='Lost').length;
  const staffLabel    = !isAdmin ? profile?.staff_name : (fStaff ? staffs.find(s=>s.id===fStaff)?.staff_name : 'All Staff');

  return (<>
    <div>
      <div style={{display:'flex',gap:8,marginBottom:16,alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <div><label style={{fontSize:12,color:'var(--text3)',marginRight:6}}>Report Date:</label>
            <span style={{fontSize:12,color:'var(--text3)'}}>From:</span>
            <DateInput value={fromDate} onChange={e=>setFromDate(e.target.value)} style={{fontSize:12}}/>
            <span style={{fontSize:12,color:'var(--text3)'}}>To:</span>
            <DateInput value={toDate} onChange={e=>setToDate(e.target.value)} style={{fontSize:12}}/>
          </div>
          {isAdmin&&(
            <select style={{width:170,fontSize:12}} value={fStaff} onChange={e=>setFStaff(e.target.value)}>
              <option value="">All Staff</option>
              {staffs.map(s=><option key={s.id} value={s.id}>{s.staff_name}</option>)}
            </select>
          )}
        </div>
        <button className="btn btn-secondary" onClick={()=>window.print()}><Printer size={14}/> Print Report</button>
        <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}><RefreshCw size={13}/> {loading?'Loading...':'Refresh'}</button>
      </div>

      <div id="print-report">
        <div style={{background:'var(--accent)',color:'#fff',borderRadius:10,padding:'14px 20px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <div style={{fontWeight:800,fontSize:16}}>Activity Report — {fmtDate(fromDate)}{fromDate!==toDate?' to '+fmtDate(toDate):''}</div>
            <div style={{fontSize:12,opacity:0.85,marginTop:2}}>{staffLabel} · Generated {fmtDate(today())}</div>
          </div>
          <Megaphone size={28} style={{opacity:0.5}}/>
        </div>

        {/* Day stats */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:16}}>
          {[
            {label:'Calls Made ('+fmtDate(fromDate)+(fromDate!==toDate?' → '+fmtDate(toDate):'')+')', value:calls.length, color:'var(--accent)', students:calls.map(cl=>({...cl.crm_students,_callDate:cl.call_date,_response:cl.response,_notes:cl.notes,_by:cl.hr_staff?.staff_name}))},
            {label:'Follow-ups Set in Range',  value:followupsDone, color:'var(--cyan)',   students:calls.filter(c=>c.next_followup_date).map(c=>({...c.crm_students,_callDate:c.call_date,_response:c.response}))},
            {label:'Follow-ups Due Today',     value:dueToday,      color:'var(--amber)',  students:all.filter(s=>s.next_followup_date===today()&&s.response!=='Converted'&&s.response!=='Lost')},
            {label:'Demo Attended (All Time)', value:demo,          color:'#8b5cf6',       students:all.filter(s=>s.response==='Demo Attended')},
            {label:'Total Converted',          value:converted,     color:'var(--green)',  students:all.filter(s=>s.response==='Converted')},
          ].map(s=>(
            <div key={s.label} onClick={()=>s.students?.length&&setDrill({title:s.label,students:s.students})}
              style={{background:'var(--surface)',border:`2px solid ${s.color}33`,borderRadius:10,padding:'14px',textAlign:'center',cursor:s.students?.length?'pointer':'default',transition:'box-shadow 0.2s'}}
              onMouseEnter={e=>{if(s.students?.length)e.currentTarget.style.boxShadow=`0 4px 16px ${s.color}44`}}
              onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
              <div style={{fontSize:28,fontWeight:900,color:s.color}}>{s.value}</div>
              <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>{s.label}</div>
              {s.students?.length>0&&<div style={{fontSize:9,color:s.color,marginTop:2}}>Click to view →</div>}
            </div>
          ))}
        </div>

        {/* Response breakdown */}
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,marginBottom:16,overflow:'hidden'}}>
          <div style={{padding:'10px 16px',background:'var(--bg3)',fontWeight:700,fontSize:13,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span>📊 Overall Response Breakdown ({all.length} students)</span>
            <button className="btn btn-ghost btn-sm" onClick={load} style={{fontSize:11}}><RefreshCw size={11}/> Refresh</button>
          </div>
          <div style={{padding:14,display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
            {RESPONSES.map(r=>(
              <div key={r} onClick={()=>(byR[r]||0)>0&&setDrill({title:r+' ('+byR[r]+' students)',students:all.filter(s=>(s.response||'Not Called')===r)})}
                style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',background:'var(--bg3)',borderRadius:8,border:`1px solid ${(RESP_COLOR[r]||'#999')}22`,cursor:(byR[r]||0)>0?'pointer':'default',transition:'background 0.15s'}}
                onMouseEnter={e=>{if((byR[r]||0)>0)e.currentTarget.style.background='var(--surface)'}}
                onMouseLeave={e=>e.currentTarget.style.background='var(--bg3)'}>
                <RBadge r={r}/><span style={{fontWeight:800,fontSize:15,color:RESP_COLOR[r]||'#6b7280'}}>{byR[r]||0}</span>
                {(byR[r]||0)>0&&<span style={{fontSize:9,color:'var(--text3)'}}>→</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Today's calls */}
        {calls.length>0&&(
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'10px 16px',background:'var(--bg3)',fontWeight:700,fontSize:13}}>📞 Calls Made {fmtDate(fromDate)}{fromDate!==toDate?' → '+fmtDate(toDate):''} ({calls.length})</div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>Student Name</th><th>College</th><th>Stream</th><th>Response</th><th>Follow-up Date</th><th>Type</th><th>Notes</th>{isAdmin&&<th>Called By</th>}</tr></thead>
                <tbody>
                  {calls.map((c,i)=>(
                    <tr key={i}>
                      <td style={{fontSize:11,color:'var(--text3)'}}>{i+1}</td>
                      <td style={{fontWeight:600,fontSize:13}}>{c.crm_students?.full_name||'—'}</td>
                      <td style={{fontSize:11}}>{c.crm_students?.college_name||'—'}</td>
                      <td><span style={{fontSize:9,padding:'1px 5px',borderRadius:3,background:c.crm_students?.stream==='Civil'?'#fef3c7':'#e0f2fe',fontWeight:700}}>{c.crm_students?.stream||'—'}</span></td>
                      <td><RBadge r={c.response}/></td>
                      <td style={{fontSize:11}}>{c.next_followup_date?fmtDate(c.next_followup_date):'—'}</td>
                      <td style={{fontSize:11}}>{c.next_followup_type||'—'}</td>
                      <td style={{fontSize:11,maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={c.notes}>{c.notes||'—'}</td>
                      {isAdmin&&<td style={{fontSize:11}}>{c.hr_staff?.staff_name||'—'}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Drill-down modal */}
    {drill&&(
      <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setDrill(null)} style={{zIndex:1000}}>
        <div className="modal" style={{maxWidth:720}}>
          <div className="modal-header">
            <h3>📊 {drill.title}</h3>
            <button className="modal-close" onClick={()=>setDrill(null)}>×</button>
          </div>
          <div className="modal-body" style={{padding:0}}>
            <div className="table-wrap" style={{maxHeight:'60vh',overflowY:'auto'}}>
              <table>
                <thead><tr><th>#</th><th>Name</th><th>College</th><th>Stream</th><th>Phone</th><th>Response</th><th>Centre</th></tr></thead>
                <tbody>
                  {drill.students.length===0&&<tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No students</td></tr>}
                  {drill.students.map((s,i)=>(
                    <tr key={i}>
                      <td style={{fontSize:11,color:'var(--text3)'}}>{i+1}</td>
                      <td style={{fontWeight:600,fontSize:13}}>{s.full_name||'—'}</td>
                      <td style={{fontSize:11,maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.college_name||'—'}</td>
                      <td><span style={{fontSize:10,padding:'1px 5px',borderRadius:3,background:s.stream==='Civil'?'#fef3c7':'#e0f2fe',fontWeight:700}}>{s.stream||'—'}</span></td>
                      <td style={{fontSize:12}}><div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontFamily:'monospace',fontSize:11}}>{s.phone||'—'}</span>{s.phone&&<CallBtn phone={s.phone}/>}{s.whatsapp&&<WABtn phone={s.whatsapp}/>}</div></td>
                      <td><RBadge r={s.response||s._response}/></td>
                      <td>{s.centre?<span className="badge badge-blue" style={{fontSize:9}}>{s.centre}</span>:'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <span style={{fontSize:12,color:'var(--text3)'}}>{drill.students.length} students</span>
            <button className="btn" onClick={()=>setDrill(null)}>Close</button>
          </div>
        </div>
      </div>
    )}
  </> );
}

// ═══════════════════════════════════════════════════════════════════
// 5. ADMIN PANEL
// ═══════════════════════════════════════════════════════════════════
function AdminPanel({ year }) {
  const [colleges,       setColleges]       = useState([]);
  const [staffs,         setStaffs]         = useState([]);
  const [showAdd,        setShowAdd]        = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editing,        setEditing]        = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [search,         setSearch]         = useState('');
  const [fCollege,       setFCollege]       = useState('');  // filter by master college name
  const [fStaff,         setFStaff]         = useState('');
  const [fStream,        setFStream]        = useState('');

  const emptyCol = { college_name:'', district:'', stream:'Civil', academic_year:String(year), total_students:'', assigned_to:'' };
  const [form, setForm] = useState(emptyCol);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  const [masterColleges, setMasterColleges] = useState([]); // from Master Data

  // Load master colleges once on mount — independent
  useEffect(()=>{
    supabase.from('colleges').select('id,college_name,district,university').order('college_name')
      .then(({data,error})=>{
        if(error){ console.error('master colleges error:',error.message); return; }
        setMasterColleges(data||[]);
      });
  },[]);

  useEffect(()=>{ loadAll(); },[]);
  async function loadAll() {
    const [{data:crmCols},{data:s}] = await Promise.all([
      supabase.from('crm_colleges').select('*,hr_staff:assigned_to(id,staff_name)').eq('academic_year', year).order('college_name'),
      supabase.from('hr_staff').select('id,staff_name,email').eq('is_active',true).order('staff_name'),
    ]);
    setColleges(crmCols||[]); setStaffs(s||[]);
  }

  async function saveCollege() {
    if (!form.college_name.trim()) { toast.error('College name required'); return; }
    setSaving(true);
    const payload = {...form, academic_year: year, total_students:form.total_students?Number(form.total_students):null, assigned_to:form.assigned_to||null};
    delete payload.hr_staff;
    const {error} = editing
      ? await supabase.from('crm_colleges').update(payload).eq('id',editing)
      : await supabase.from('crm_colleges').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success(editing?'Updated ✅':'College added ✅'); setShowAdd(false); setEditing(null); setForm(emptyCol); loadAll(); }
    setSaving(false);
  }

  async function reassign(col, newStaffId) {
    const newStaff = staffs.find(s=>s.id===newStaffId)?.staff_name||'Unassigned';
    const oldStaff = col.hr_staff?.staff_name||'Unassigned';
    if (!window.confirm(`Reassign "${col.college_name} (${col.stream})" from ${oldStaff} → ${newStaff}?\n\nAll students and call history will transfer.`)) return;
    await supabase.from('crm_colleges').update({assigned_to:newStaffId||null}).eq('id',col.id);
    // Reassign all students in this college+stream
    await supabase.from('crm_students').update({assigned_to:newStaffId||null})
      .eq('college_name',col.college_name).eq('stream',col.stream).eq('academic_year', year);
    toast.success(`✅ Reassigned to ${newStaff}`);
    loadAll();
  }

  // Carry the college list + staff assignments forward from another year (students are NOT copied)
  async function copyColleges() {
    const from = window.prompt(`Copy colleges (with assigned staff) INTO ${year} from which year?`, String(year-1));
    if (!from) return;
    const fy = Number(from);
    if (!fy || fy === year) { toast.error('Enter a different valid year'); return; }
    const {data:src, error} = await supabase.from('crm_colleges').select('*').eq('academic_year', fy);
    if (error) { toast.error(error.message); return; }
    if (!(src||[]).length) { toast.error(`No colleges found in ${fy}`); return; }
    const {data:cur} = await supabase.from('crm_colleges').select('college_name,stream').eq('academic_year', year);
    const have = new Set((cur||[]).map(c=>`${c.college_name}||${c.stream}`));
    const rows = src.filter(c=>!have.has(`${c.college_name}||${c.stream}`)).map(c=>{
      const { id: _id, created_at: _ca, updated_at: _ua, hr_staff: _hs, ...rest } = c;
      return { ...rest, academic_year: year, total_students: null };
    });
    if (!rows.length) { toast('All colleges from '+fy+' already exist in '+year); return; }
    if (!window.confirm(`Copy ${rows.length} college entries from ${fy} into ${year}?`)) return;
    const {error:ie} = await supabase.from('crm_colleges').insert(rows);
    if (ie) toast.error(ie.message); else { toast.success(`✅ ${rows.length} colleges copied to ${year}`); loadAll(); }
  }

  const filtered = colleges.filter(c=>
    (!search  ||c.college_name?.toLowerCase().includes(search.toLowerCase()))
    &&(!fCollege||c.college_name===fCollege)
    &&(!fStaff  ||c.assigned_to===fStaff||(fStaff==='unassigned'&&!c.assigned_to))
    &&(!fStream ||c.stream===fStream)
  );

  // Group by college name for summary
  const byCollege = {};
  colleges.forEach(c=>{ if(!byCollege[c.college_name]) byCollege[c.college_name]=[]; byCollege[c.college_name].push(c); });

  return (
    <div>
      {/* Summary */}
      <div style={{display:'grid',gridTemplateColumns:(window.innerWidth<=768)?'1fr 1fr':'repeat(4,1fr)',gap:10,marginBottom:16}}>
        {[
          {label:'Total Colleges',  value:Object.keys(byCollege).length, color:'var(--accent)'},
          {label:'College+Stream Entries', value:colleges.length,        color:'var(--cyan)'},
          {label:'Unassigned',      value:colleges.filter(c=>!c.assigned_to).length, color:'var(--amber)'},
          {label:'Total Staff',     value:staffs.length,                 color:'var(--green)'},
        ].map(s=>(
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div style={{fontSize:22,fontWeight:800,color:s.color,marginTop:4}}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{display:'flex',gap:6,marginBottom:16,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
          <div className="search-bar" style={{minWidth:180}}><Search/><input placeholder="Search college..." value={search} onChange={e=>setSearch(e.target.value)}/></div>
          <select style={{width:220,fontSize:12,padding:'4px 6px'}} value={fCollege} onChange={e=>setFCollege(e.target.value)}>
            <option value="">— All Colleges ({masterColleges.length > 0 ? masterColleges.length+' loaded' : 'loading...'}) —</option>
            {masterColleges.length > 0
              ? masterColleges.map(mc=><option key={mc.id} value={mc.college_name}>{mc.college_name}</option>)
              : <option disabled>Add colleges in Master Data → Colleges first</option>
            }
          </select>
          <select style={{width:110,fontSize:12,padding:'4px 6px'}} value={fStream} onChange={e=>setFStream(e.target.value)}>
            <option value="">All Streams</option>
            <option value="Civil">🏗️ Civil</option>
            <option value="Mechanical">⚙️ Mechanical</option>
          </select>
          <select style={{width:150,fontSize:12,padding:'4px 6px'}} value={fStaff} onChange={e=>setFStaff(e.target.value)}>
            <option value="">All Staff</option>
            <option value="unassigned">⚠️ Unassigned</option>
            {staffs.map(s=><option key={s.id} value={s.id}>{s.staff_name}</option>)}
          </select>
          {(search||fCollege||fStream||fStaff)&&<button className="btn btn-ghost btn-sm" onClick={()=>{setSearch('');setFCollege('');setFStream('');setFStaff('');}}>✕ Clear</button>}
          <span style={{fontSize:12,color:'var(--text3)'}}>{filtered.length} entries</span>
        </div>
        <div style={{display:'flex',gap:6}}>
          <button className="btn btn-secondary" onClick={copyColleges}>📋 Copy colleges from another year</button>
          <button className="btn btn-secondary" onClick={()=>setShowBulkUpload(true)}><Upload size={14}/> 📥 Bulk Upload (Excel)</button>
          <button className="btn btn-primary" onClick={()=>{setEditing(null);setForm(emptyCol);setShowAdd(true);}}><Plus size={14}/> Add College</button>
        </div>
      </div>

      {/* Staff email check */}
      {staffs.some(s=>!s.email)&&(
        <div style={{background:'#fffbeb',border:'1px solid #f59e0b',borderRadius:8,padding:'10px 16px',marginBottom:14,fontSize:12,color:'#92400e'}}>
          ⚠️ <strong>Some staff don't have email linked:</strong> {staffs.filter(s=>!s.email).map(s=>s.staff_name).join(', ')} — Update their email in HR → Staff Details so they can log in to CRM.
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>College Name</th>
              <th style={{background:'#fef3c7'}}>Stream ⭐</th>
              <th>District</th>
              <th>Acad. Year</th>
              <th>Students</th>
              <th>Assigned To (Email)</th>
              <th>Reassign →</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length===0&&<tr><td colSpan={9}><div className="empty-state"><Building2 size={32}/><h3>No colleges yet</h3><p>Add colleges and assign to staff members</p></div></td></tr>}
            {filtered.map((c,i)=>(
              <tr key={c.id} style={{background:!c.assigned_to?'rgba(245,158,11,0.05)':undefined}}>
                <td style={{fontSize:11,color:'var(--text3)'}}>{i+1}</td>
                <td style={{fontWeight:600,fontSize:13}}>{c.college_name}</td>
                <td>
                  <span style={{background:c.stream==='Civil'?'#fef3c7':'#e0f2fe',color:c.stream==='Civil'?'#92400e':'#0369a1',borderRadius:6,padding:'3px 10px',fontSize:11,fontWeight:700}}>
                    {c.stream==='Civil'?'🏗️':'⚙️'} {c.stream}
                  </span>
                </td>
                <td style={{fontSize:12}}>{c.district||'—'}</td>
                <td style={{fontSize:12}}>{c.academic_year||'—'}</td>
                <td style={{textAlign:'center',fontSize:13,fontWeight:600}}>{c.total_students||'—'}</td>
                <td>
                  {c.hr_staff?.staff_name
                    ? <div>
                        <span className="badge badge-blue" style={{fontSize:10}}>{c.hr_staff.staff_name}</span>
                        {staffs.find(s=>s.id===c.assigned_to)?.email&&<div style={{fontSize:9,color:'var(--text3)',marginTop:2}}>{staffs.find(s=>s.id===c.assigned_to)?.email}</div>}
                      </div>
                    : <span style={{color:'var(--amber)',fontSize:11,fontWeight:700}}>⚠️ Unassigned</span>}
                </td>
                <td>
                  <select style={{width:150,fontSize:11,padding:'3px 6px',borderColor:!c.assigned_to?'var(--amber)':'var(--border)'}}
                    value={c.assigned_to||''} onChange={e=>reassign(c,e.target.value||null)}>
                    <option value="">— Unassign —</option>
                    {staffs.map(s=><option key={s.id} value={s.id}>{s.staff_name}{!s.email?' ⚠️':''}</option>)}
                  </select>
                </td>
                <td>
                  <div style={{display:'flex',gap:4}}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>alert(`College: ${c.college_name}\nStream: ${c.stream}\nStaff: ${c.hr_staff?.staff_name||'Unassigned'}\nStudents: ${c.total_students||0}\nDistrict: ${c.district||'—'}`)} title="View"><Eye size={13}/></button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{setEditing(c.id);setForm({...emptyCol,...c,total_students:c.total_students!=null?String(c.total_students):'',assigned_to:c.assigned_to||''});setShowAdd(true);}}><Edit2 size={13}/></button>
                    <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(!window.confirm('Delete college from admin panel? Students will remain assigned to this college name.'))return;await supabase.from('crm_colleges').delete().eq('id',c.id);loadAll();}}><Trash2 size={13}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showBulkUpload && <BulkUploadModal year={year} staffs={staffs} onClose={()=>setShowBulkUpload(false)} onDone={()=>{setShowBulkUpload(false);loadAll();}}/>}

      {/* Add/Edit College Modal */}
      {showAdd&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowAdd(false)}>
          <div className="modal modal-lg" style={{maxWidth:600}}>
            <div className="modal-header">
              <span className="modal-title">{editing?'Edit':'Add'} College Entry</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowAdd(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{background:'#eff6ff',border:'1px solid #93c5fd',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#1d4ed8'}}>
                💡 <strong>Each college needs two separate entries</strong> — one for Civil and one for Mechanical. Each can be assigned to a different staff member.
              </div>
              <div className="form-grid">
                <div className="form-group full">
                  <label>College Name * <span style={{fontSize:10,color:'var(--accent)'}}>← from Master Data → Colleges</span></label>
                  <select value={form.college_name} onChange={e=>sf('college_name',e.target.value)}>
                    <option value="">— Select College from Master Data —</option>
                    {masterColleges.length>0
                      ? masterColleges.map(mc=><option key={mc.id} value={mc.college_name}>{mc.college_name}{mc.district?' — '+mc.district:''}</option>)
                      : <option disabled value="">⚠️ No colleges found — add in Master Data → Colleges first</option>
                    }
                  </select>
                </div>
                <div className="form-group"><label>Stream * <span style={{fontSize:10,color:'var(--red)'}}>Required</span></label>
                  <select value={form.stream} onChange={e=>sf('stream',e.target.value)} style={{borderColor:form.stream==='Civil'?'#d97706':'#0891b2',fontWeight:700}}>
                    <option value="Civil">🏗️ Civil</option>
                    <option value="Mechanical">⚙️ Mechanical</option>
                    {['Automobile','Aeronautical','Electrical','Other'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>District</label><select value={form.district||''} onChange={e=>sf('district',e.target.value)}><option value=''>Select District</option>{['Thiruvananthapuram','Kollam','Pathanamthitta','Alappuzha','Kottayam','Idukki','Ernakulam','Thrissur','Palakkad','Malappuram','Kozhikode','Wayanad','Kannur','Kasaragod','Other'].map(d=><option key={d} value={d}>{d}</option>)}</select></div>
                <div className="form-group"><label>Data Year</label>
                  <input value={year} disabled/>
                </div>
                <div className="form-group"><label>Total Students (approx.)</label><input type="number" value={form.total_students} onChange={e=>sf('total_students',e.target.value)}/></div>
                <div className="form-group"><label>Assign To Staff</label>
                  <select value={form.assigned_to} onChange={e=>sf('assigned_to',e.target.value)}>
                    <option value="">— Unassigned —</option>
                    {staffs.map(s=><option key={s.id} value={s.id}>{s.staff_name}{!s.email?' (no email ⚠️)':''}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveCollege} disabled={saving}>{saving?'Saving...':editing?'Update':'Add College'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// BULK UPLOAD MODAL — one Excel handles colleges + assignments + students
// ═══════════════════════════════════════════════════════════════════
function BulkUploadModal({ year, staffs, onClose, onDone }) {
  const fileRef  = useRef();
  const [step,   setStep]   = useState('guide');   // guide | preview | result
  const [parsed, setParsed] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  // Download sample Excel
  function downloadSample() {
    const headers = [
      'College Name',
      'Stream (Civil/Mechanical)',
      'Assigned To (Staff Name)',
      'Student Name',
      'Phone',
      'WhatsApp',
      'Email',
      'Year of Passing',
      'Scholarship Marks',
      'Source',
      'Centre',
    ];
    const samples = [
      ['Mar Baselios College EKM', 'Civil',       'SMITHA',  'Arun Kumar K',    '9876543210', '9876543210', 'arun@email.com', String(year), '45', 'NACTET Scholarship', 'EKM'],
      ['Mar Baselios College EKM', 'Civil',       'SMITHA',  'Priya Rajan',     '9876543211', '',           '',              String(year), '38', 'NACTET Scholarship', 'EKM'],
      ['Mar Baselios College EKM', 'Mechanical',  'AMIYA',   'Rahul Nair',      '9876543212', '9876543212', '',              String(year), '42', 'NACTET Scholarship', 'TVM'],
      ['MES College Ernakulam',    'Civil',       'RAKHI',   'Sneha Mathew',    '9876543213', '9876543213', '',              String(year-1), '50', 'Reference',          'EKM'],
      ['MES College Ernakulam',    'Mechanical',  'ANEECIA', 'Vishnu Prasad',   '9876543214', '',           '',              String(year-1), '33', 'NACTET Scholarship', 'TVM'],
    ];
    const data = [headers, ...samples];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [25,18,18,20,14,14,22,12,14,18,8].map(w=>({wch:w}));
    XLSX.utils.book_append_sheet(wb, ws, 'CRM Bulk Upload');
    XLSX.writeFile(wb, 'CRM_Bulk_Upload_Format.xlsx');
    toast.success('Sample downloaded!');
  }

  async function handleFile(e) {
    const file = e.target.files?.[0]; if (!file) return;
    const buf  = await file.arrayBuffer();
    const wb   = XLSX.read(buf,{type:'array'});
    const raw  = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]],{header:1,defval:''});
    if (raw.length<2) { toast.error('File empty'); return; }

    const hdrs = raw[0].map(h=>String(h||'').toLowerCase().trim());
    const get  = (row,terms) => {
      const t = Array.isArray(terms)?terms:[terms];
      const i = hdrs.findIndex(h=>t.some(x=>h.includes(x)));
      return i>=0 ? String(row[i]||'').trim() : '';
    };

    const rows = raw.slice(1).filter(r=>r.some(v=>v!==''&&v!=null));

    // Parse rows
    const parsed = rows.map((row,ri) => {
      const exactGet = (header) => {
        const i = hdrs.findIndex(h => h === header.toLowerCase().trim());
        return i >= 0 ? String(row[i]||'').trim() : '';
      };
      return {
        _rowNum:      ri+2,
        college_name: exactGet('college name'),
        stream:       exactGet('stream (civil/mechanical)') || exactGet('stream'),
        assigned_to:  exactGet('assigned to (staff name)') || exactGet('assigned to'),
        full_name:    exactGet('student name'),
        phone:        exactGet('phone'),
        whatsapp:     exactGet('whatsapp'),
        email:        exactGet('email'),
        year_of_passing: exactGet('year of passing'),
        scholarship_marks: Number(exactGet('scholarship marks'))||null,
        source:       exactGet('source') || 'NACTET Scholarship',
        centre:       exactGet('centre') || '',
      };
    });

    // Validate
    const errors = [];
    parsed.forEach(r => {
      if (!r.college_name) errors.push(`Row ${r._rowNum}: College Name missing`);
      if (!r.stream)       errors.push(`Row ${r._rowNum}: Stream missing`);
      if (!r.full_name)    errors.push(`Row ${r._rowNum}: Student Name missing`);
    });

    // Map staff names to IDs
    const staffMap = {};
    staffs.forEach(s => { staffMap[s.staff_name.toUpperCase()] = s.id; });

    const validRows = parsed.filter(r=>r.college_name&&r.stream&&r.full_name);
    const mappedRows = validRows.map(r => ({
      ...r,
      assigned_to_id: staffMap[r.assigned_to.toUpperCase()] || null,
      stream_normalized: r.stream.toLowerCase().includes('mech') ? 'Mechanical' :
                         r.stream.toLowerCase().includes('civil') ? 'Civil' :
                         r.stream.toLowerCase().includes('auto') ? 'Automobile' :
                         r.stream,
    }));

    // Summarize colleges to be created
    const collegeSet = {};
    mappedRows.forEach(r => {
      const key = `${r.college_name}||${r.stream_normalized}`;
      if (!collegeSet[key]) collegeSet[key] = { college_name:r.college_name, stream:r.stream_normalized, assigned_to:r.assigned_to_id, staff_name:r.assigned_to, student_count:0 };
      collegeSet[key].student_count++;
    });

    setParsed({ rows:mappedRows, colleges:Object.values(collegeSet), errors, total:validRows.length });
    setStep('preview');
    fileRef.current.value='';
  }

  async function runUpload() {
    if (!parsed) return;
    setUploading(true);
    let colCreated=0, colUpdated=0, stuCreated=0, errors=[];

    try {
      // Step 1: Get existing crm_colleges
      const {data:existingCols} = await supabase.from('crm_colleges').select('id,college_name,stream,assigned_to').eq('academic_year', year);
      const existingMap = {};
      (existingCols||[]).forEach(c=>{ existingMap[`${c.college_name}||${c.stream}`] = c; });

      // Step 2: Create or update colleges
      const collegeIdMap = {}; // "CollegeName||Stream" → id
      for (const col of parsed.colleges) {
        const key = `${col.college_name}||${col.stream}`;
        const existing = existingMap[key];
        if (existing) {
          // Update assignment if provided
          if (col.assigned_to && existing.assigned_to !== col.assigned_to) {
            await supabase.from('crm_colleges').update({assigned_to:col.assigned_to}).eq('id',existing.id);
            colUpdated++;
          }
          collegeIdMap[key] = existing.id;
        } else {
          // Create new college entry
          const {data:newCol, error} = await supabase.from('crm_colleges').insert({
            college_name: col.college_name,
            stream:       col.stream,
            assigned_to:  col.assigned_to||null,
            academic_year: year,
          }).select().single();
          if (error) { errors.push(`College "${col.college_name} ${col.stream}": ${error.message}`); }
          else { collegeIdMap[key]=newCol.id; colCreated++; }
        }
      }

      // Step 3: Insert students in chunks of 50
      const studentRows = parsed.rows.map(r => ({
        full_name:        r.full_name,
        college_name:     r.college_name,
        stream:           r.stream_normalized,
        phone:            r.phone||null,
        whatsapp:         r.whatsapp||null,
        email:            r.email||null,
        year_of_passing:  r.year_of_passing||null,
        scholarship_marks:r.scholarship_marks||null,
        source:           r.source,
        response:         'Not Called',
        assigned_to:      r.assigned_to_id||null,
        academic_year:    year,
      }));

      for (let i=0; i<studentRows.length; i+=50) {
        const chunk = studentRows.slice(i,i+50);
        // Use upsert with onConflict to skip duplicates by phone+college_name
        const {error, data:inserted} = await supabase.from('crm_students').upsert(chunk, {onConflict:'phone,college_name,academic_year',ignoreDuplicates:true}).select('id');
        if (error) errors.push(`Students batch ${Math.floor(i/50)+1}: ${error.message}`);
        else stuCreated += (inserted?.length||0);
      }

      setResult({ colCreated, colUpdated, stuCreated, errors });
      setStep('result');
    } catch(err) {
      toast.error('Upload failed: '+err.message);
    }
    setUploading(false);
  }

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-lg" style={{maxWidth:780,maxHeight:'94vh',overflowY:'auto'}}>
        <div className="modal-header">
          <span className="modal-title">📥 Bulk Upload — Colleges + Students in One Go</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-body">

          {/* STEP 1 — GUIDE */}
          {step==='guide' && (
            <div>
              {/* How it works */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
                {[
                  {num:'1',title:'Download Sample',desc:'Get the Excel template with correct column format',color:'var(--accent)'},
                  {num:'2',title:'Fill Your Data',desc:'Add all colleges, assign staff names, and add all student details in one sheet',color:'#8b5cf6'},
                  {num:'3',title:'Upload & Done',desc:'System auto-creates colleges, assigns users, and adds all students at once',color:'var(--green)'},
                ].map(s=>(
                  <div key={s.num} style={{background:'var(--bg3)',borderRadius:10,padding:'14px',textAlign:'center',border:`1px solid ${s.color}33`}}>
                    <div style={{width:32,height:32,background:s.color,color:'#fff',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:900,fontSize:16,margin:'0 auto 10px'}}>{s.num}</div>
                    <div style={{fontWeight:700,fontSize:13,marginBottom:6}}>{s.title}</div>
                    <div style={{fontSize:11,color:'var(--text3)'}}>{s.desc}</div>
                  </div>
                ))}
              </div>

              {/* Column format */}
              <div style={{background:'var(--accent-light)',border:'1px solid var(--accent)',borderRadius:10,padding:'14px 16px',marginBottom:16}}>
                <div style={{fontWeight:700,marginBottom:10,fontSize:13}}>📋 Excel Column Format (10 columns)</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:6}}>
                  {[
                    ['College Name','Full college name — same rows for same college+stream'],
                    ['Stream','Civil / Mechanical / Automobile etc.'],
                    ['Assigned To','Staff name exactly as in HR (e.g. SMITHA, AMIYA)'],
                    ['Student Name','Full name of student'],
                    ['Phone','Mobile number'],
                    ['WhatsApp','If different from phone, else leave blank'],
                    ['Email','Email ID'],
                    ['Year of Passing','e.g. 2026'],
                    ['Scholarship Marks','Marks scored in MechPrize/CPrize exam'],
                    ['Source','NACTET Scholarship / Reference / etc.'],
                  ].map(([col,hint])=>(
                    <div key={col} style={{background:'var(--surface)',borderRadius:6,padding:'8px 10px',display:'flex',gap:8,alignItems:'flex-start'}}>
                      <span style={{fontWeight:700,fontSize:12,color:'var(--accent)',minWidth:120,flexShrink:0}}>{col}</span>
                      <span style={{fontSize:11,color:'var(--text3)'}}>{hint}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Example preview */}
              <div style={{background:'var(--bg3)',borderRadius:10,padding:'12px 14px',marginBottom:16,overflowX:'auto'}}>
                <div style={{fontWeight:700,fontSize:12,marginBottom:8,color:'var(--text2)'}}>📄 Example rows:</div>
                <table style={{fontSize:10,borderCollapse:'collapse',width:'100%'}}>
                  <thead>
                    <tr style={{background:'var(--accent)',color:'#fff'}}>
                      {['College Name','Stream','Assigned To','Student Name','Phone','WhatsApp','Email','Year','Marks','Source'].map(h=>(
                        <th key={h} style={{padding:'4px 8px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Mar Baselios EKM','Civil','SMITHA','Arun Kumar K','9876543210','9876543210','',String(year),'45','NACTET'],
                      ['Mar Baselios EKM','Civil','SMITHA','Priya Rajan','9876543211','','',String(year),'38','NACTET'],
                      ['Mar Baselios EKM','Mechanical','AMIYA','Rahul Nair','9876543212','','',String(year),'42','NACTET'],
                      ['MES College EKM','Civil','RAKHI','Sneha Mathew','9876543213','','',String(year-1),'50','Reference'],
                    ].map((row,i)=>(
                      <tr key={i} style={{background:i%2===0?'var(--surface)':'var(--bg3)'}}>
                        {row.map((cell,j)=><td key={j} style={{padding:'4px 8px',whiteSpace:'nowrap'}}>{cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{fontSize:10,color:'var(--text3)',marginTop:6}}>💡 Each row = one student. Same college repeats across rows. System auto-groups by college+stream.</div>
              </div>

              {/* Staff list reminder */}
              <div style={{background:'#fffbeb',border:'1px solid #f59e0b',borderRadius:8,padding:'10px 14px',marginBottom:16}}>
                <div style={{fontWeight:700,fontSize:12,color:'#92400e',marginBottom:6}}>⚠️ Available Staff Names (use exact spelling in "Assigned To" column):</div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {staffs.map(s=>(
                    <span key={s.id} style={{background:'#fff',border:'1px solid #f59e0b',borderRadius:6,padding:'3px 10px',fontSize:12,fontWeight:600,color:'#92400e'}}>
                      {s.staff_name}{!s.email&&<span style={{color:'#dc2626'}}> ⚠️no email</span>}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{display:'flex',gap:10}}>
                <button className="btn btn-secondary" style={{flex:1}} onClick={downloadSample}><Download size={14}/> Download Sample Excel</button>
                <button className="btn btn-primary" style={{flex:1}} onClick={()=>fileRef.current?.click()}><Upload size={14}/> Upload Filled Excel</button>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={handleFile}/>
              </div>
            </div>
          )}

          {/* STEP 2 — PREVIEW */}
          {step==='preview' && parsed && (
            <div>
              {/* Summary */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:16}}>
                {[
                  {label:'College+Stream Entries',value:parsed.colleges.length,color:'var(--accent)'},
                  {label:'Students to Import',    value:parsed.total,          color:'var(--green)'},
                  {label:'Rows with Errors',       value:parsed.errors.length, color:parsed.errors.length>0?'var(--red)':'var(--green)'},
                ].map(s=>(
                  <div key={s.label} style={{background:'var(--bg3)',borderRadius:10,padding:'14px',textAlign:'center'}}>
                    <div style={{fontSize:28,fontWeight:900,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Errors */}
              {parsed.errors.length>0&&(
                <div style={{background:'#fef2f2',border:'1px solid var(--red)',borderRadius:8,padding:'10px 14px',marginBottom:14}}>
                  <div style={{fontWeight:700,color:'var(--red)',marginBottom:6}}>⚠️ {parsed.errors.length} rows skipped:</div>
                  {parsed.errors.slice(0,5).map((e,i)=><div key={i} style={{fontSize:12,color:'var(--red)'}}>{e}</div>)}
                  {parsed.errors.length>5&&<div style={{fontSize:11,color:'var(--text3)'}}>...and {parsed.errors.length-5} more</div>}
                </div>
              )}

              {/* College preview */}
              <div style={{fontWeight:700,fontSize:13,marginBottom:8}}>🎓 Colleges to be created/updated:</div>
              <div className="table-wrap" style={{maxHeight:180,overflowY:'auto',marginBottom:14}}>
                <table>
                  <thead><tr><th>College Name</th><th>Stream</th><th>Assign To</th><th>Students</th></tr></thead>
                  <tbody>
                    {parsed.colleges.map((c,i)=>(
                      <tr key={i}>
                        <td style={{fontWeight:600,fontSize:12}}>{c.college_name}</td>
                        <td><span style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:c.stream==='Civil'?'#fef3c7':'#e0f2fe',fontWeight:700}}>{c.stream==='Civil'?'🏗️':'⚙️'} {c.stream}</span></td>
                        <td style={{fontSize:12}}>{c.staff_name||<span style={{color:'var(--amber)'}}>⚠️ Not matched</span>}</td>
                        <td style={{textAlign:'center',fontWeight:700,color:'var(--accent)'}}>{c.student_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Student preview */}
              <div style={{fontWeight:700,fontSize:13,marginBottom:8}}>👥 First 10 students preview:</div>
              <div className="table-wrap" style={{maxHeight:200,overflowY:'auto'}}>
                <table>
                  <thead><tr><th>#</th><th>Name</th><th>College</th><th>Stream</th><th>Phone</th><th>Year</th><th>Marks</th><th>Assigned To</th></tr></thead>
                  <tbody>
                    {parsed.rows.slice(0,10).map((r,i)=>(
                      <tr key={i}>
                        <td style={{fontSize:11,color:'var(--text3)'}}>{i+1}</td>
                        <td style={{fontWeight:600,fontSize:12}}>{r.full_name}</td>
                        <td style={{fontSize:11,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.college_name}</td>
                        <td><span style={{fontSize:9,padding:'1px 5px',borderRadius:3,background:r.stream_normalized==='Civil'?'#fef3c7':'#e0f2fe',fontWeight:700}}>{r.stream_normalized}</span></td>
                        <td style={{fontSize:11,fontFamily:'monospace'}}>{r.phone||'—'}</td>
                        <td style={{fontSize:11}}>{r.year_of_passing||'—'}</td>
                        <td style={{fontSize:11,textAlign:'center',fontWeight:700,color:'var(--accent)'}}>{r.scholarship_marks||'—'}</td>
                        <td style={{fontSize:11}}>{r.assigned_to||<span style={{color:'var(--amber)'}}>Unassigned</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parsed.rows.length>10&&<div style={{fontSize:11,color:'var(--text3)',marginTop:6}}>...and {parsed.rows.length-10} more students</div>}
            </div>
          )}

          {/* STEP 3 — RESULT */}
          {step==='result' && result && (
            <div style={{textAlign:'center',padding:'20px 0'}}>
              <div style={{fontSize:52,marginBottom:12}}>{result.errors.length===0?'🎉':'⚠️'}</div>
              <div style={{fontWeight:800,fontSize:20,marginBottom:20}}>
                {result.errors.length===0 ? 'Bulk Upload Complete!' : 'Upload Done with Some Errors'}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:14,marginBottom:20}}>
                {[
                  {label:'Colleges Created',  value:result.colCreated,  color:'var(--accent)'},
                  {label:'Assignments Updated',value:result.colUpdated, color:'var(--cyan)'},
                  {label:'Students Added',    value:result.stuCreated,  color:'var(--green)'},
                ].map(s=>(
                  <div key={s.label} style={{background:'var(--bg3)',borderRadius:10,padding:'14px'}}>
                    <div style={{fontSize:32,fontWeight:900,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:12,color:'var(--text3)',marginTop:4}}>{s.label}</div>
                  </div>
                ))}
              </div>
              {result.errors.length>0&&(
                <div style={{background:'#fef2f2',border:'1px solid var(--red)',borderRadius:8,padding:'12px 16px',textAlign:'left',marginBottom:14}}>
                  <div style={{fontWeight:700,color:'var(--red)',marginBottom:6}}>⚠️ Errors ({result.errors.length}):</div>
                  {result.errors.map((e,i)=><div key={i} style={{fontSize:12,color:'var(--red)',marginBottom:3}}>{e}</div>)}
                </div>
              )}
              <button className="btn btn-primary" style={{marginTop:8}} onClick={onDone}>✅ Done — View Updated List</button>
            </div>
          )}

        </div>

        <div className="modal-footer">
          {step==='guide'   && <button className="btn btn-secondary" onClick={onClose}>Cancel</button>}
          {step==='preview' && <>
            <button className="btn btn-secondary" onClick={()=>setStep('guide')}>← Back</button>
            <button className="btn btn-primary" onClick={runUpload} disabled={uploading||parsed?.total===0}>
              {uploading ? <><RefreshCw size={13} style={{animation:'spin 1s linear infinite'}}/> Uploading...</> : `📥 Upload ${parsed?.total} Students + ${parsed?.colleges.length} Colleges`}
            </button>
          </>}
          {step==='result' && <button className="btn btn-secondary" onClick={onClose}>Close</button>}
        </div>
      </div>
    </div>
  );
}